// ──────────────────────────────────────────────────────────────────────
//  NEW CARD RENDERER (skeleton-driven, replaces clone-and-override)
//
//  Reads data-vb-* attributes from snippets/brum-card-skeleton.liquid.
//  No template-product dependency. No phantom data possible.
//
//  Exposes: window.BrumCard.apply(card, hit)
//
//  This sits at the top of the file so window.BrumCard is defined
//  before the existing renderResults() loop runs. Step 3 swaps the
//  call site from applyCardData(li, hit) to BrumCard.apply(li, hit).
//  Until that swap, this module is dormant — defined but not called.
// ──────────────────────────────────────────────────────────────────────
(function () {
  'use strict';

  function escapeAttr(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function setText(card, sel, value) {
    var el = card.querySelector(sel);
    if (el) el.textContent = value == null ? '' : String(value);
  }
  function setLink(card, sel, url, ariaLabel) {
    var el = card.querySelector(sel);
    if (!el) return;
    el.setAttribute('href', url);
    if (ariaLabel != null && el.hasAttribute('aria-label')) {
      el.setAttribute('aria-label', ariaLabel);
    }
  }
  function setAllLinks(card, sel, url, ariaLabel) {
    card.querySelectorAll(sel).forEach(function (el) {
      el.setAttribute('href', url);
      if (ariaLabel != null && el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', ariaLabel);
      }
    });
  }
  var IMG_PLACEHOLDER = 'https://cdn.shopify.com/s/files/1/0564/2823/2786/files/missing-img.svg?v=1776315686';
  function setImage(card, sel, src, alt) {
    var el = card.querySelector(sel);
    if (!el) return;
    el.src = src || IMG_PLACEHOLDER;
    el.alt = alt || '';
  }
  function setSlot(card, sel, shown, value) {
    var el = card.querySelector(sel);
    if (!el) return;
    if (shown) {
      var strong = el.querySelector('strong');
      if (strong) strong.textContent = value == null ? '' : String(value);
      else el.textContent = value == null ? '' : String(value);
      el.style.display = '';
    } else {
      el.style.display = 'none';
    }
  }
  function formatPrice(amount) {
    var n = Math.round(Number(amount || 0));
    return '€' + n.toLocaleString('de-DE', { maximumFractionDigits: 0 });
  }
  function prettyTag(s) {
    return String(s == null ? '' : s)
      .replace(/[-_]+/g, ' ')
      .replace(/\b([a-z])/g, function (m) { return m.toUpperCase(); });
  }
  function firstNonEmpty(hit, a, b) {
    var v = hit[a];
    if (v != null && v !== '') return String(v);
    if (b) { v = hit[b]; if (v != null && v !== '') return String(v); }
    return '';
  }
  // ────────────────────────────────────────────────────────────────────
  //  ICON-GRID — registry pattern, dispatched by collection handle.
  //
  //  Add a new category by registering its builder on ICON_GRID_BUILDERS.
  //  Each builder receives the Typesense hit and returns an array of
  //  HTML strings (one per cell). Use iconCell(name, text) for cells.
  //
  //  Currently registered: car
  //  TODO future:         bike, truck, e-bike (see commented stubs below)
  // ────────────────────────────────────────────────────────────────────

  var ICON_SVG = {
    calendar:     '<svg class="icon icon-34" aria-hidden="true"><use href="#icon-calendar"/></svg>',
    mileage:      '<svg class="icon icon-34" aria-hidden="true"><use href="#icon-mileage"/></svg>',
    fuel:         '<svg class="icon icon-34" aria-hidden="true"><use href="#icon-fuel"/></svg>',
    'fuel-electric': '<svg class="icon icon-34" aria-hidden="true"><use href="#icon-fuel-electric"/></svg>',
    transmission: '<svg class="icon icon-34" aria-hidden="true"><use href="#icon-transmission"/></svg>',
    range:        '<svg class="icon icon-34" aria-hidden="true"><use href="#icon-range"/></svg>',
    owners:       '<svg class="icon icon-34" aria-hidden="true"><use href="#icon-owners"/></svg>',
    power:        '<svg class="icon icon-34" aria-hidden="true"><use href="#icon-power"/></svg>',
    cc:           '<svg class="icon icon-34" aria-hidden="true"><use href="#icon-cubic-capacity"/></svg>',
    cylinders:    '<svg class="icon icon-34" aria-hidden="true"><use href="#icon-cylinders"/></svg>'
  };

  function iconCell(name, text) {
    var svg = ICON_SVG[name];
    if (!svg) return '';
    return '<div class="icon-item flex items-center" data-vb-icon="' + name + '">' +
             svg +
             '<strong>' + escapeAttr(text) + '</strong>' +
           '</div>';
  }

  var ICON_GRID_BUILDERS = Object.create(null);

  // ── CAR ──────────────────────────────────────────────────────────────
  // Mirrors snippets/product-card.liquid lines 494-608 for non-bike products.
  // Slot order (matches live): calendar, mileage, fuel, transmission|range, owners, power.
  // EVs swap transmission → range. Empty slots are NOT rendered.
  ICON_GRID_BUILDERS.car = function (hit) {
    var cells = [];

    var month = hit.vehicle_details__first_registration_month;
    var year  = hit.vehicle_details__first_registration_year || hit.year;
    if (month && year) cells.push(iconCell('calendar', month + ' / ' + year));

    var mileage = Number(hit.mileage || 0);
    if (mileage > 0) cells.push(iconCell('mileage', mileage.toLocaleString('de-DE') + ' km'));

    var fuel = firstNonEmpty(hit, 'fuel_type', 'vehicle_details__fuel_type');
    var isEv  = !!fuel && fuel.toLowerCase().indexOf('electric') !== -1;
    if (fuel) cells.push(iconCell(isEv ? 'fuel-electric' : 'fuel', fuel));

    // EV → range, otherwise → transmission (mutex)
    var range = firstNonEmpty(hit, 'vehicle_details__range_km', 'vehicle_details__range');
    var trans = firstNonEmpty(hit, 'transmission', 'vehicle_details__transmission');
    if (isEv && range) cells.push(iconCell('range', range + ' km'));
    else if (trans)    cells.push(iconCell('transmission', trans));

    var owners = firstNonEmpty(hit, 'owners', 'vehicle_details__number_of_owners');
    if (owners) cells.push(iconCell('owners', owners));

    var hp = Number(hit.power_hp || hit.vehicle_details__power || 0);
    if (hp > 0) cells.push(iconCell('power', Math.round(hp * 1.341) + ' HP'));

    return cells;
  };

  // ── BIKE (motorbikes) ─────────────────────────────────────────────────
  // Same layout as cars, but the two car-specific slots are swapped for
  // bike specs: fuel → cubic capacity, transmission → cylinder count.
  // Slot order: calendar, mileage, cubic capacity, cylinders, owners, power.
  ICON_GRID_BUILDERS.bike = function (hit) {
    var cells = [];

    var month = hit.vehicle_details__first_registration_month;
    var year  = hit.vehicle_details__first_registration_year || hit.year;
    if (month && year) cells.push(iconCell('calendar', month + ' / ' + year));
    else if (year)     cells.push(iconCell('calendar', String(year)));

    var mileage = Number(hit.mileage || 0);
    if (mileage > 0) cells.push(iconCell('mileage', mileage.toLocaleString('de-DE') + ' km'));

    var cc = Number(hit.vehicle_details__cubic_capacity || hit.cubic_capacity || 0);
    if (cc > 0) cells.push(iconCell('cc', cc.toLocaleString('de-DE') + ' cc'));

    var cyl = firstNonEmpty(hit, 'vehicle_details__cylinder_count', 'cylinder_count');
    if (cyl) cells.push(iconCell('cylinders', cyl + ' cyl'));

    var owners = firstNonEmpty(hit, 'owners', 'vehicle_details__number_of_owners');
    if (owners) cells.push(iconCell('owners', owners));

    var hp = Number(hit.power_hp || hit.vehicle_details__power || 0);
    if (hp > 0) cells.push(iconCell('power', hp + ' HP'));

    return cells;
  };


  var CURRENT_CATEGORY = (function () {
    var m = (location.pathname || '').match(/\/collections\/([^\/?#]+)/);
    return m ? decodeURIComponent(m[1]).toLowerCase() : 'car';
  })();

  // Pick the icon-grid builder per CARD, not per page: a bike in a mixed
  // search result (/collections/all?q=) should show bike specs while the
  // cars around it show car specs. We match the hit's own collection
  // handles against the registered builders; fall back to the page's
  // collection, then to car.
  function categoryForHit(hit) {
    var colls = Array.isArray(hit.collections) ? hit.collections : [];
    for (var i = 0; i < colls.length; i++) {
      var h = String(colls[i]).toLowerCase();
      if (ICON_GRID_BUILDERS[h]) return h;
    }
    return ICON_GRID_BUILDERS[CURRENT_CATEGORY] ? CURRENT_CATEGORY : 'car';
  }
  function buildIconGrid(hit) {
    var builder = ICON_GRID_BUILDERS[categoryForHit(hit)] || ICON_GRID_BUILDERS.car;
    return builder(hit).join('');
  }
  function applyCardData(card, hit) {
    var url   = '/products/' + encodeURIComponent(hit.handle || '');
    var title = hit.title || '';

    if (hit.id != null) card.setAttribute('data-product-id', hit.id);

    setAllLinks(card, '.js-prod-link', url, title);
    setLink(card, '[data-vb-image-link]', url, title);
    setLink(card, '[data-vb-title-link]', url, null);

    setText(card, '[data-vb-title-mobile]', title.length > 30 ? title.slice(0, 30) + '...' : title);
    setText(card, '[data-vb-title-desktop]', title);

    setImage(card, '[data-vb-image]', hit.image_url, hit.image_alt || title);

    var cond = String(hit.vehicle_details__vehicle_condition || hit.vehicle_condition || '').toLowerCase().trim();
    var condEl = card.querySelector('[data-vb-cond]');
    if (condEl) condEl.setAttribute('data-vb-cond', cond);

    setText(card, '[data-vb-price]', formatPrice(hit.price));

    var compareEl = card.querySelector('[data-vb-compare]');
    if (compareEl) {
      var cv = Number(hit.compare_at_price || 0);
      var pv = Number(hit.price || 0);
      var hasDiscount = cv > pv;
      if (hasDiscount) compareEl.textContent = formatPrice(cv);
      compareEl.hidden = !hasDiscount;
    }

    setText(card, '[data-vb-vendor]', hit.vendor || '');
    var grid = card.querySelector('[data-vb-icon-grid]');
    if (grid) grid.innerHTML = buildIconGrid(hit);

    var logo = card.querySelector('[data-vb-seller-logo]');
    if (logo) {
      var hasLogo = !!hit.seller_info__profile_pic;
      if (hasLogo) logo.src = hit.seller_info__profile_pic;
      logo.hidden = !hasLogo;
    }

    var ratingBlock = card.querySelector('[data-vb-rating-block]');
    var rating = Number(hit.seller_info__rating);
    var showRating = Number.isFinite(rating) && rating > 0 && hit.seller_info__display_ratings !== false;
    if (ratingBlock) ratingBlock.hidden = !showRating;
    if (showRating) {
      setText(card, '[data-vb-rating-number]', rating.toFixed(1));
      var stars = card.querySelector('[data-vb-stars-inner]');
      if (stars) {
        stars.setAttribute('data-rating', rating);
        stars.style.width = Math.max(0, Math.min(100, (rating / 5) * 100)) + '%';
      }
    }

    var phoneEl = card.querySelector('[data-vb-phone]');
    if (phoneEl) {
      var hasPhone = !!hit.seller_info__phone_number;
      if (hasPhone) phoneEl.setAttribute('href', 'tel:+' + String(hit.seller_info__phone_number).replace(/[^\d+]/g, ''));
      phoneEl.hidden = !hasPhone;
    }
    var mailEl = card.querySelector('[data-vb-email]');
    if (mailEl) {
      var hasEmail = !!hit.seller_info__email;
      if (hasEmail) mailEl.setAttribute('href', 'mailto:' + hit.seller_info__email);
      mailEl.hidden = !hasEmail;
    }

    var mapBtn   = card.querySelector('[data-vb-map-btn]');
    var mapPopup = card.querySelector('[data-vb-map-popup]');
    var iframe   = card.querySelector('[data-vb-map-iframe]');
    var lat = hit.seller_info__latitude, lng = hit.seller_info__longitude;
    var hasGeo = lat != null && lng != null && lat !== '' && lng !== '';
    if (mapBtn) mapBtn.hidden = !hasGeo;
    if (hasGeo) {
      if (mapPopup) mapPopup.classList.remove('show-popup');
      if (iframe)   iframe.src = 'https://maps.google.com/maps?q=' +
                                 encodeURIComponent(lat) + ',' + encodeURIComponent(lng) +
                                 '&z=14&output=embed';
    } else {
      if (mapPopup) mapPopup.remove();
    }

    var wishlist = card.querySelector('[data-vb-wishlist]');
    if (wishlist) {
      wishlist.setAttribute('data-product-id', hit.id);
      if (hit.vehicle_details__vehicle_id != null) {
        wishlist.setAttribute('data-vehicle-id', hit.vehicle_details__vehicle_id);
      }
      wishlist.hidden = false;
    }

    var compareInput = card.querySelector('[data-vb-compare-input]');
    var compareLabel = card.querySelector('[data-vb-compare-label]');
    if (compareInput) {
      var compareId = 'compare-' + hit.id;
      compareInput.id = compareId;
      compareInput.setAttribute('data-product-id', hit.id);
      compareInput.setAttribute('data-product-url', url);
      compareInput.checked = false;
      if (compareLabel) compareLabel.setAttribute('for', compareId);
    }

    var featuresRaw = [];
    if (hit.vehicle_details__additionalmetadata) {
      featuresRaw = String(hit.vehicle_details__additionalmetadata)
        .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    }
    var features = featuresRaw.slice(0, 6);
    var chipsHtml = features.length === 0 ? '' : features.map(function (label) {
      return '<div class="brum-item">' +
               '<svg class="icon icon-18" aria-hidden="true"><use href="#icon-tick"/></svg>' +
               '<span data-feat="' + escapeAttr(label) + '">' + escapeAttr(prettyTag(label)) + '</span>' +
             '</div>';
    }).join('');
    var chipsDesktop = card.querySelector('[data-vb-chips-desktop]');
    var chipsMobile  = card.querySelector('[data-vb-chips-mobile]');
    if (chipsDesktop) chipsDesktop.innerHTML = chipsHtml;
    if (chipsMobile)  chipsMobile.innerHTML  = chipsHtml;

    var soldOut = card.querySelector('[data-vb-sold-out-wrapper]');
    if (soldOut) {
      soldOut.hidden = hit.available !== false;
    }
  }

  window.BrumCard = { apply: applyCardData };
})();

(function () {
  var root = document.querySelector("[data-brum]");
  if (!root) return;

  // -------- Config --------
  var PROXY_BASE   = root.dataset.proxyBase;
  var PER_PAGE     = parseInt(root.dataset.perPage, 10) || 24;
  var DEFAULT_SORT = root.dataset.defaultSort || "created_at:desc";
  var CCY          = root.dataset.currencySymbol || "€";
  var POWER_UNIT   = root.dataset.powerUnit || "kW";
  var GRID_SEL     = root.dataset.gridSelector || "#brum-products";
  var BOUNDS = {
    year:    { min: parseInt(root.dataset.yearMin, 10) || 1990,
               max: parseInt(root.dataset.yearMax, 10) || (new Date()).getFullYear() + 1 },
    price:   { min: 0, max: parseInt(root.dataset.priceMax, 10)   || 200000 },
    mileage: { min: 0, max: parseInt(root.dataset.mileageMax, 10) || 500000 },
    power:   { min: 0, max: parseInt(root.dataset.powerMax, 10)   || 700 },
    cubic_capacity: { min: 0, max: 8000 },
    range: { min: 0, max: 800 },
    battery: { min: 0, max: 150 },
    cylinders: { min: 1, max: 12 },
  };
  var BATTERY_UNIT = " kWh";
  if (detectCollectionHandle() === "e-bike") {
    BOUNDS.battery = { min: 0, max: 1500 };
    BATTERY_UNIT = " Wh";
  }

   function setPowerUnit(unit) {
    if (unit === POWER_UNIT) return;
    POWER_UNIT = unit;
    root.querySelectorAll('[data-power-unit-toggle] button').forEach(function (btn) {
      btn.classList.toggle('is-active', btn.dataset.unit === unit);
    });
    setRangeInput("power_min", state.range.power_min);
    setRangeInput("power_max", state.range.power_max);
    updateAllSliderStates();
    if (typeof renderChips === "function") renderChips();
  }
var BRUM_COLORS = {
    "beige":"#D8C69E","black":"#1E1E1E","blue":"#2E5BE6","brown":"#5B4327",
    "gold":"#C6A94D","green":"#6FA83B","grey":"#9C9C9C","gray":"#9C9C9C",
    "orange":"#EF7C2A","purple":"#7B4DF0","red":"#E24339","silver":"#D3D6D8",
    "white":"#FFFFFF","yellow":"#EFD32E","other":"#B0B0B0",
    "white/silver/grey":"#D3D6D8","orange/yellow":"#EF7C2A","purple/pink":"#7B4DF0"
  };
  var BRUM_ICONS = {
    "sasija": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/sasija.png?v=1767110808",
    "šasija": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/sasija.png?v=1767110808",
    "chassis": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/sasija.png?v=1767110808",
    "acura": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/acura-svgrepo-com.svg?v=1757356787",
    "abarth": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/abarth.svg?v=1763751946",
    "alpine": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/alpine-automobile.svg?v=1763755936",
    "autobianchi": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/autobianchi-stemma_2806bb23-d61b-47a5-927b-845f7473cb16.svg?v=1763793792",
    "audi": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/audi-11-logo-svgrepo-com.svg?v=1757354712",
    "aprilia": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/idI16tPdHR_1764865273294.svg?v=1764865429",
    "alfa romeo": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/alfa-romeo-4-logo-svgrepo-com.svg?v=1757356787",
    "aston martin": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/aston-martin-1_55ed0949-c0e2-4e69-ab48-2c0812d38208.svg?v=1757362387",
    "bmw": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/bmw-svgrepo-com.svg?v=1757356787",
    "byd": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/byd-1.svg?v=1757356787",
    "baic": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/idZkVimetX_logos.png?v=1763764201",
    "benelli": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Untitled_design-18.png?v=1765478939",
    "bugatti": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/bugatti-logo.svg?v=1757360791",
    "bentley": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/bentley-logo-svgrepo-com.svg?v=1761893289",
    "buick": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Buick__8.svg?v=1761894444",
    "cadillac": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-74.svg?v=1763797788",
    "chevrolet": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/chevrolet-11.svg?v=1761899894",
    "cagiva": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Cagiva__4.svg?v=1764871450",
    "chrysler": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/chrysler-2.svg?v=1761901623",
    "citroën": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/citroen-svgrepo-com_5dc9a60f-7676-4893-8ef8-9a9df43c6292.svg?v=1761893176",
    "cupra": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-61.svg?v=1761916699",
    "cfmoto": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/CFMOTO_logo_black_symbol_sRGB.png?v=1764871814",
    "dodge": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/dodge-ram-logo-svgrepo-com.svg?v=1757360791",
    "dacia": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-75.svg?v=1763798159",
    "dongfeng": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/0489.HK.svg?v=1763759336",
    "daihatsu": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/daihatsu-4_7959faad-255a-4b8c-a02d-9b34ded213ab.svg?v=1763751946",
    "ds automobiles": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-70.svg?v=1763765027",
    "daewoo": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/daewoo-svgrepo-com.svg?v=1763763848",
    "ducati": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/ducati-logo-brandlogos.net_ue7hc2mb1.svg?v=1763762053",
    "ferrari": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/ferrari-ges-logo-svgrepo-com.svg?v=1757360791",
    "fiat": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/fiat-svgrepo-com.svg?v=1757362601",
    "ford": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Ford_Romania-Logo.wine.svg?v=1761895403",
    "forthing": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/forthing.png?v=1763795304",
    "honda": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-64.svg?v=1761919474",
    "hyundai": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-65.svg?v=1761920507",
    "hongqi": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/hongqui.png?v=1763792710",
    "husquarna": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/husqvarna-seeklogo.svg?v=1764871586",
    "hummer": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/hummer.svg?v=1763759593",
    "harley davidson": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/harley-davidson-11_a5ae7271-8f4f-4ed9-9699-c54caa94c862.svg?v=1764865789",
    "infiniti": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/infiniti-svgrepo-com.svg?v=1761896307",
    "lamborghini": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/lamborghini.svg?v=1757360791",
    "tesla": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/tesla-svgrepo-com.svg?v=1760469148",
    "toyota": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-66.svg?v=1761921216",
    "tata": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/tata-logo.svg?v=1763758776",
    "tm racing": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/tm-racing-logo-vector.svg?v=1764870023",
    "triumph": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/logo-triumph.svg?v=1764865789",
    "land rover": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/land-rover-2.svg?v=1757360792",
    "lexus": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/lexus-svgrepo-com-2.svg?v=1761896367",
    "lotus": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/lotus-1.svg?v=1761901195",
    "lincoln": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Lincoln_Motor_logomark-OkE9lSVG2_brandlogos.net.svg?v=1763761918",
    "ligier": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-69.svg?v=1763763266",
    "lancia": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/lancia-2.svg?v=1761901321",
    "kia": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/kia-svgrepo-com_7fade423-d2a4-48a3-8c10-9a8d063ee8b2.svg?v=1761896432",
    "kgm": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-71.svg?v=1763794536",
    "ktm": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/ktm-svgrepo-com.svg?v=1765479198",
    "kawasaki": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/kawasaki-logo-brandlogos.net_v9xrdx2hw.svg?v=1764873673",
    "gmc": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/gmc-1.svg?v=1761900316",
    "gasgas": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/GASGAS-OL7EnfrMN_brandlogos.net.svg?v=1764871104",
    "genesis": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/genesis-motor-logo-brandlogos.net_vtaexkm6k.svg?v=1763761569",
    "geely": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/geely-logo-2_0d93a0bc-cc74-4c19-aa77-f17a790382cc.svg?v=1763766660",
    "isuzu": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/isuzu-2.svg?v=1761900498",
    "jeep": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/jeep-svgrepo-com_db380df3-2210-4149-8a5c-788f73a0f9e3.svg?v=1761896534",
    "jaecoo": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/jaecoo-seeklogo.svg?v=1763749151",
    "jaguar": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/jaguar-cars.svg?v=1763756040",
    "mercedes-benz": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/mercedes-benz-9-logo-svgrepo-com.svg?v=1757356786",
    "mg": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/mg.svg?v=1763754105",
    "mclaren": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/mclaren-red.svg?v=1763796049",
    "microcar": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/microcar-logo-brandlogos.net_jnqfdiyre.svg?v=1763754614",
    "mini": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/bmw-mini-1.svg?v=1757360791",
    "mitsubishi": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/mitsubishi.svg?v=1757361523",
    "mv agusta": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/mv-agusta-logo_fea13058-ec38-495d-b7a3-1a6787265160.svg?v=1764865789",
    "moto guzzi": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/moto-guzzi-logo-2016_99fae5ee-f35a-40d7-9f99-e8fbb1091b29.svg?v=1764865959",
    "mazda": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/mazda-alt-svgrepo-com.svg?v=1760469873",
    "mahindra": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/mahindra-mahindra-logo.svg?v=1763750472",
    "maserati": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/logo-della-maserati-1.svg?v=1763750701",
    "morgan motors": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/images.png?v=1763762874",
    "nissan": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/nissan-svgrepo-com.svg?v=1760469252",
    "peugeot": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/peugeot-10.svg?v=1757361618",
    "porsche": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/porsche-6-logo-svgrepo-com.svg?v=1760469377",
    "piaggio": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/piaggio-2_631482b3-8da9-4fab-b186-525183cbfd4e.svg?v=1764865789",
    "puch": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/puch-logo.svg?v=1763761084",
    "royal enfield": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/puch-logo.svg?v=1763761084",
    "opel": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/opel-6-logo-svgrepo-com.svg?v=1760469304",
    "omoda": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/omoda-seeklogo.png?v=1763755289",
    "polestar": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/PSNY.svg?v=1763759874",
    "pontiac": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/pontiac-7.svg?v=1763760037",
    "renault": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/renault-7.svg?v=1757362472",
    "rivian": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/rivian.svg?v=1761900907",
    "rolls-royce": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/rolls-royce.svg?v=1761901029",
    "skoda": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/skoda-6.svg?v=1757361132",
    "suzuki": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Suzuki-OxmgVAEvD_brandlogos.net.svg?v=1764872115",
    "subaru": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/subaru-logo-brandlogos.net.png?v=1761914826",
    "smart": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-72.svg?v=1763796981",
    "seat": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-60.svg?v=1761915782",
    "sym": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/sym-sanyang-motor-logo-brandlogos.net_ld9ey3wy5.svg?v=1764870833",
    "ycf": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Ycf_Logo_Vector_svg.png?v=1764874079",
    "volkswagen": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/volkswagen-svgrepo-com.svg?v=1757356787",
    "volvo": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/volvo.svg?v=1757361668",
    "leapmotor": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/9863.HK.svg?v=1763756295",
    "lucid": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/LCID.svg?v=1763756421",
    "lynk & co": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Lynk___Co_2016_logo.svg?v=1763756580",
    "lada": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/lada.png?v=1763795465",
    "kymco": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/KYMCO-O0B5NswiK_brandlogos.net.svg?v=1764870949",
    "yamaha": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-91.svg?v=1764870331",
    "zastava": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-77.svg?v=1763799596",
    "limousine": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-sedan-car-2020255.png?v=1765143781",
    "estate": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-cuv-car-2020252.png?v=1765144216",
    "suv / off-road / pick-up": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-suv-car-2020259.png?v=1765143541",
    "coupe / sports car": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-supercar-2020249.png?v=1765143956",
    "hatchback": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-hatchback-car-2020246.svg?v=1765143696",
    "van / minibus": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-mini-van-2020248.png?v=1765143602",
    "cabrio": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-cabriolet-car-2020254.png?v=1765143651",
    "small car": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-mini-car-2020261.png?v=1765143472",
    "damaged": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-120.svg?v=1767257096",
    "chopper": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-bobber.svg?v=1782852194",
    "electric scooter": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/e-scooter_8ae2e546-ed02-4efd-ac37-8505f34e3cd9.svg?v=1782855444",
    "scooter": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-maxi-scooter.svg?v=1782852194",
    "touring": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-touring.svg?v=1782852194",
    "snowmobile": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-snowmobile.svg?v=1782852194",
    "enduro": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-adventure.svg?v=1782852194",
    "moped": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/moped.svg?v=1782852194",
    "three-wheeler": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/three-wheeler.svg?v=1782852193",
    "supermoto": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-supermoto.svg?v=1782852194",
    "sportbike": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-supersports.svg?v=1782852194",
    "motocross": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-motorbike.svg?v=1782852194",
    "naked bike": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-standard.svg?v=1782853916",
    "atv": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-atv.svg?v=1782852194",
    "trial": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-trial.svg?v=1782852194",
    "minimoto": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-mini-bike.svg?v=1782852194",
    "go-kart": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-go-kart.svg?v=1782852193",
    "can-am": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/BRP.svg?v=1765474707",
    "beta": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/betamotor-logo-brandlogos.net_ex8kragnq.svg?v=1765477654",
    "gilera": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Gilera-O760nv1hH_brandlogos.net.svg?v=1765477767",
    "barton": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Untitled_design-19.png?v=1765479615",
    "benda": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Untitled_design-20.png?v=1765480027",
    "dkw": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Untitled_design-21.png?v=1765483346",
    "fantic": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Fantic_idHFv7f53O_1.svg?v=1765484033",
    "fb mondial": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Untitled_design-22.png?v=1765484246",
    "husaberg": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/husaberg-logo-brandlogos.net_08ycnhb1p.svg?v=1765485082",
    "hyosung": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/hyosung-logo-brandlogos.net_f5z1p9yn7.svg?v=1765485382",
    "indian": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Indian_Motorcycle-OeeTEFLpr_brandlogos.net.svg?v=1765485546",
    "jawa": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/jawa-moto-logo-brandlogos.net_kmqlzxidk.svg?v=1765485716",
    "keeway": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Untitled_design-25.png?v=1765486186",
    "derbi": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/derbi-logo-brandlogos.net_b2heh2r11.svg?v=1765519395",
    "garelli": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Untitled_design-24.png?v=1765484525",
    "ebroh": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Logo-ebroh.png?v=1765524950",
    "iveco": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/Iveco-OtAfLVmG8_brandlogos.net.svg?v=1767107546",
    "man": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/MAN_Truck_and_Bus-OQbdEeWP1_brandlogos.net.svg?v=1767107640",
    "maxus": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/id7pMy-zYg_logos.svg?v=1767107853",
    "avtovleka": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/avtovleka_f6e43e4a-f1f3-44d7-9c8e-70a113cd0781.png?v=1767110742",
    "box": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/box_ecd01224-3df5-43a2-b55e-d19a04e53fe7.png?v=1767110808",
    "kiper": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/kiper.png?v=1767110939",
    "crossbar": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/crossbar3.svg?v=1776063392",
    "fully": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/full2.svg?v=1776064068",
    "low step": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/low-step.svg?v=1776064535",
    "trapeze": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/trapeze.svg?v=1776064676",
    "mountain bike": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/full.svg?v=1776088397",
    "fat bike": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-124.svg?v=1776090602",
    "cross bike": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/trail.svg?v=1776092701",
    "trekking bike": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-126.svg?v=1776093111",
    "cargo bike": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/svgviewer-output-128.svg?v=1776093651",
    "folding bike": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/fould.svg?v=1776093762",
    "city bike": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/noun-bicycle-26116.svg?v=1776094100",
    "hladilni furgon": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/hladilni_furgon.png?v=1767111058",
    "minibus": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/minibus.png?v=1767112912",
    "tovorni furgon": "https://cdn.shopify.com/s/files/1/0564/2823/2786/files/tovorni_furgon.png?v=1767112913"
  };
  var FACETS = ["make","model","fuel_type","body_type","transmission","drivetrain","exterior_color","vehicle_condition","vehicle_details__emission_class","vehicle_details__interior_material","vehicle_details__headlight_type","vehicle_details__climatisation","vendor","doors","seats","owners","vehicle_details__interior_primary_color","vehicle_details__airbags","vehicle_details__cruise_control","vehicle_details__stroke_number","vehicle_details__cooling_system","vehicle_details__driving_mode","vehicle_details__cylinder_count","vehicle_details__length","vehicle_details__height","vehicle_details__load_capacity_kg","safety_features","lights_features","comfort_features","seat_features","multimedia_features","sport_features","extras_features","charge_time_hours","fast_charge_minutes","battery_warranty_bucket","vehicle_details__frame_material","vehicle_details__frame_size","vehicle_details__wheel_size","vehicle_details__motor_position","vehicle_details__battery_manufacturer","vehicle_details__battery_position","gear_type","audience","vehicle_details__frame_shape"];
  var TAG_FACETS = new Set(["safety_features","lights_features","comfort_features","seat_features","multimedia_features","sport_features","extras_features","gear_type","audience"]);
  var NUMERIC_SORT = new Set(["charge_time_hours","fast_charge_minutes","seats","doors","owners","vehicle_details__cylinder_count"]);
  var FACET_VALUE_ORDER = {
    seat_features: [
      "heated-seats-front", "heated-seats-rear",
      "electrically-adjustable-seats-front", "electrically-adjustable-seats-rear",
      "seat-ventilation", "lumbar-support", "sport-seats"
    ]
  };
  var FACET_GROUPS = {
    battery_group: ["vehicle_details__battery_manufacturer", "vehicle_details__battery_position", "battery_warranty_bucket"],
    frame_group: ["vehicle_details__frame_shape", "vehicle_details__frame_material", "vehicle_details__frame_size"],
    seats_group:  ["seats", "seat_features"],
    colors_group: ["exterior_color", "vehicle_details__interior_primary_color", "vehicle_details__interior_material"],
    lights_group: ["vehicle_details__headlight_type", "lights_features"],
    safety_group: ["vehicle_details__airbags", "safety_features"],
    comfort_group: ["comfort_features", "vehicle_details__climatisation", "vehicle_details__cruise_control", "multimedia_features"]
  };
  var HIDE_WHEN_EMPTY = new Set([]);
  var GREY_EXCLUDE = new Set(["make", "model"]);
  function greyable(f) { return !GREY_EXCLUDE.has(f); }
  var FACET_UNIVERSE = {};
  var RANGES = ["year_min","year_max","price_min","price_max","mileage_min","mileage_max","power_min","power_max","cubic_capacity_min","cubic_capacity_max","range_min","range_max","battery_min","battery_max"];
  var SLIDERS = [
    { name:"year",           minKey:"year_min",           maxKey:"year_max",           fmt: fmtPlain },
    { name:"price",          minKey:"price_min",          maxKey:"price_max",          fmt: fmtMoney },
    { name:"mileage",        minKey:"mileage_min",        maxKey:"mileage_max",        fmt: fmtKm },
    { name:"power",          minKey:"power_min",          maxKey:"power_max",          fmt: fmtPower },
    { name:"cubic_capacity", minKey:"cubic_capacity_min", maxKey:"cubic_capacity_max", fmt: fmtCC },
    { name:"range", minKey:"range_min", maxKey:"range_max", fmt: fmtKm },
    { name:"battery", minKey:"battery_min", maxKey:"battery_max", fmt: fmtKwh },
  ];

  var DISCRETE_SLIDERS = { cylinders: true };
  var SLIDER_VALUES = {};
  function snapToValues(name, n) {
    var vals = SLIDER_VALUES[name];
    if (!vals || !vals.length) return n;
    var best = vals[0], bd = Math.abs(n - vals[0]);
    for (var i = 1; i < vals.length; i++) { var d = Math.abs(n - vals[i]); if (d < bd) { bd = d; best = vals[i]; } }
    return best;
  }

  var state = { facets:{}, range:{}, sort:DEFAULT_SORT, page:1, q:"", mmFocusMake:null, modelMake:{}, seriesMake:{}, viewAllMakes:new Set(), geo:{ active:false, lat:null, lng:null, radius:50 } };

  // Derive the series group for a model. BMW now; extend per brand later. "" = no group (standalone).
  function modelSeries(make, model) {
    var mk = String(make == null ? "" : make).trim();
    var md = String(model == null ? "" : model).trim();
    if (/^bmw$/i.test(mk)) {
      if (/series$/i.test(md)) return "";          // literal "2 Series" etc -> standalone
      if (/^X\d/i.test(md)) return "X Series";
      if (/^Z\d/i.test(md)) return "Z Series";
      if (/^i(X|\d)/i.test(md)) return "i Series";
      var m = md.match(/^M?\s*(\d)/);              // 116, 320, M340, M2, 1er M Coupé
      if (m) return m[1] + " Series";
      return "";
    }
    if (/mercedes/i.test(mk)) {                    // refine once Mercedes models are split the same way
      if (/class$/i.test(md)) return "";
      var c = md.match(/^([A-Za-z]{1,3})[\s-]?\d/);
      if (c) return c[1].toUpperCase() + "-Class";
      return "";
    }
    return "";
  }

  FACETS.forEach(function (f) { state.facets[f] = new Set(); });
  RANGES.forEach(function (r) { state.range[r] = ""; });

  // -------- Make banner (paid placement) --------
  // The make-banner section emits a normalized make -> image-url map as JSON.
  // We swap the banner img by the active make: a single selected Make facet, or
  // a make detected in the search query (?q=). Falls back to the default image.
  var BRUM_BANNER_MAP = (function () {
    try {
      var el = document.querySelector("[data-brum-banner-map]");
      return el ? JSON.parse(el.textContent || "{}") : {};
    } catch (e) { return {}; }
  })();
  function normalizeMake(s) {
    return String(s == null ? "" : s).toLowerCase().replace(/[^a-z0-9]/g, "");
  }
  // Pick the active make for the banner. Priority: exactly one selected Make
  // facet, else a promoted make found in the search query.
  function activeBannerMake() {
    var selected = state.facets.make ? Array.from(state.facets.make) : [];
    if (selected.length === 1) return normalizeMake(selected[0]);
    if (state.q) {
      var nq = normalizeMake(state.q);
      var best = "";
      Object.keys(BRUM_BANNER_MAP).forEach(function (k) {
        if (k !== "default" && nq.indexOf(k) !== -1 && k.length > best.length) best = k;
      });
      if (best) return best;
    }
    return "";
  }
  function updateMakeBanner() {
    var img = document.querySelector("[data-brum-banner-img]");
    var src = document.querySelector("[data-brum-banner-source]"); // desktop <source>
    var def = BRUM_BANNER_MAP.default;
    if (!img || !def || !def.d) return;            // need a default desktop image
    var key = activeBannerMake();
    var entry = (key && BRUM_BANNER_MAP[key]) || def;
    var dUrl = entry.d || def.d;                   // desktop
    var mUrl = entry.m || entry.d || def.m || def.d; // mobile (falls back to desktop)
    // The default keeps its width/height attrs (CLS-free first paint). On a swap
    // to another make we drop them so the new image renders at its own natural
    // ratio (height:auto) rather than being squeezed into the default's ratio.
    if (src && dUrl && src.getAttribute("srcset") !== dUrl) {
      src.srcset = dUrl;
      src.removeAttribute("width"); src.removeAttribute("height");
    }
    if (mUrl && img.getAttribute("src") !== mUrl) {
      img.src = mUrl;
      img.removeAttribute("width"); img.removeAttribute("height");
    }
  }
  var lastData = null;
  var debounceTimer = null;
  var facetSearchTerms = {};
  var gridStale = false;
  var seenFacets = {};
  
  // -------- Helpers --------
  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;" }[c];
    });
  }
  function fmtPlain(n) { return Number(n).toLocaleString('de-DE'); }
  function fmtMoney(n) { return CCY + Number(n).toLocaleString('de-DE'); }
  function fmtKm(n)    { return Number(n).toLocaleString('de-DE') + " km"; }
  function fmtKwh(n)   { return Number(n).toLocaleString('de-DE') + BATTERY_UNIT; }
  function fmtCC(n)    { return Number(n).toLocaleString('de-DE') + " cc"; }

  var KW_TO_HP = 1.341;   // 1 kW ≈ 1.341 HP (mechanical)
  var HP_TO_KW = 0.7457;
  function displayPower(kw) {
    if (kw === "" || kw == null) return "";
    var n = Number(kw); if (!Number.isFinite(n)) return "";
    return POWER_UNIT === "HP" ? Math.round(n * KW_TO_HP) : Math.round(n);
  }
  function parseUserPower(userInput) {
    if (userInput === "" || userInput == null) return "";
    var n = Number(userInput); if (!Number.isFinite(n)) return "";
    return POWER_UNIT === "HP" ? Math.round(n * HP_TO_KW) : Math.round(n);
  }
  function fmtPower(n) { return Number(displayPower(n)).toLocaleString('de-DE') + " " + POWER_UNIT; }
  function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

  function syncFacetsMake() {
    var s = new Set(state.viewAllMakes);
    Object.keys(state.modelMake).forEach(function (mod) {
      s.add(state.modelMake[mod]);
    });
    Object.keys(state.seriesMake).forEach(function (ser) {
      s.add(state.seriesMake[ser]);
    });
    state.facets.make = s;
  }
  // -------- URL <-> state --------
  function readUrl() {
    var p = new URLSearchParams(location.search);
    FACETS.forEach(function (f) {

      var values = [];
      p.getAll(f).forEach(function (raw) {
        (raw || "").split("|").forEach(function (piece) {
          var v = piece.trim();
          if (v) values.push(v);
        });
      });
      state.facets[f] = new Set(values);
    });
    RANGES.forEach(function (r) { state.range[r] = p.get(r) || ""; });
    state.sort = p.get("sort") || DEFAULT_SORT;
    state.q = p.get("q") || "";
    state.page = parseInt(p.get("page") || "1", 10);

    state.modelMake = {};
    var mmRaw = p.get("model_make") || "";
    if (mmRaw) {
      mmRaw.split("|").forEach(function (pair) {
        var idx = pair.indexOf(":");
        if (idx > 0) {
          var mod = pair.slice(0, idx).trim();
          var mk  = pair.slice(idx + 1).trim();
          if (mod && mk) state.modelMake[mod] = mk;
        }
      });
    }

    state.seriesMake = {};
    var smRaw = p.get("series_make") || "";
    if (smRaw) {
      smRaw.split("|").forEach(function (pair) {
        var idx = pair.indexOf(":");
        if (idx > 0) {
          var ser = pair.slice(0, idx).trim();
          var smk = pair.slice(idx + 1).trim();
          if (ser && smk) state.seriesMake[ser] = smk;
        }
      });
    }

    state.viewAllMakes = new Set();
    state.facets.make.forEach(function (mk) {
      var hasAnyModel = Object.keys(state.modelMake).some(function (mod) {
        return state.modelMake[mod] === mk;
      });
      var hasAnySeries = Object.keys(state.seriesMake).some(function (ser) {
        return state.seriesMake[ser] === mk;
      });
      if (!hasAnyModel && !hasAnySeries) state.viewAllMakes.add(mk);
    });

    if (state.facets.make.size === 0 && state.facets.model.size > 0) {
      state.facets.model = new Set();
    }

    RANGES.forEach(function (r) {
      var el = root.querySelector('[data-range="' + r + '"]');
      if (el) el.value = state.range[r];
    });
  }
  function buildParams() {
    var p = new URLSearchParams();
    FACETS.forEach(function (f) {

      var values = Array.from(state.facets[f]);
      if (values.length > 0) p.set(f, values.join("|"));
    });
    RANGES.forEach(function (r) {
      if (state.range[r] !== "" && state.range[r] !== null) p.set(r, state.range[r]);
    });
    if (state.sort && state.sort !== DEFAULT_SORT) p.set("sort", state.sort);
    if (state.q) p.set("q", state.q);
    if (state.page > 1) p.set("page", String(state.page));
    p.set("per_page", String(PER_PAGE));

    var col = detectCollectionHandle();
    if (col) p.set("collection", col);

    if (state.mmFocusMake) p.set("focus_make", state.mmFocusMake);

    var pairs = Object.keys(state.modelMake).map(function (mod) {
      return mod + ":" + state.modelMake[mod];
    });
    if (pairs.length > 0) p.set("model_make", pairs.join("|"));

    var sPairs = Object.keys(state.seriesMake).map(function (ser) {
      return ser + ":" + state.seriesMake[ser];
    });
    if (sPairs.length > 0) p.set("series_make", sPairs.join("|"));

    var drawerEl = root.querySelector("[data-drawer]");
    if (drawerEl && drawerEl.classList.contains("is-open")) {
  p.set("facets_only", "1");
}
if (state.geo && state.geo.active && state.geo.lat != null && state.geo.lng != null) {
  p.set("near_lat", String(state.geo.lat));
  p.set("near_lng", String(state.geo.lng));
  p.set("near_radius", String(state.geo.radius || 50));
}
return p;
}

  function detectCollectionHandle() {
    var m = (location.pathname || "").match(/\/collections\/([^\/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }
  function writeUrl() {
    var p = buildParams();

    p.delete("per_page");
    p.delete("collection");
    var qs = p.toString();
    history.replaceState(null, "", location.pathname + (qs ? "?" + qs : ""));
  }


  var countTweenRaf = null;
  function animateCount(newVal) {
    var inline = root.querySelector("[data-count-inline]");
    var countEl = root.querySelector("[data-count]");
    function write(v) {
      var U = window.BRUM_UI || {};
      var label = v === 1 ? (U.result || "result") : (U.results || "results");
      if (inline) inline.textContent = v.toLocaleString('de-DE') + " " + label;
      if (countEl) countEl.textContent = v.toLocaleString('de-DE') + " " + label;
    }
    var fromText = inline ? inline.textContent : "";
    var fromNum = parseInt(fromText.replace(/[^\d]/g, ""), 10);
    if (isNaN(fromNum)) { write(newVal); return; }
    if (Math.abs(newVal - fromNum) < 5) { write(newVal); return; }
    if (countTweenRaf) cancelAnimationFrame(countTweenRaf);
    var start = performance.now();
    var duration = 280;
    function step(t) {
      var p = Math.min(1, (t - start) / duration);
      var eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      var cur = Math.round(fromNum + (newVal - fromNum) * eased);
      write(cur);
      if (p < 1) countTweenRaf = requestAnimationFrame(step);
      else countTweenRaf = null;
    }
    countTweenRaf = requestAnimationFrame(step);
  }


  function refresh(append) {
    if (!append) state.page = 1;
    var dMm = root.querySelector("[data-drawer]");
    if (dMm) {
      var mmSel = state.facets.model.size > 0 || Object.keys(state.seriesMake).length > 0 || state.viewAllMakes.size > 0;
      dMm.setAttribute("data-mm-has-selection", mmSel ? "true" : "false");
    }
    var url = PROXY_BASE + "/search?" + buildParams().toString();

    var isFacetsOnly = url.indexOf("facets_only=1") !== -1;
    fetch(url, { credentials:"same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        lastData = data;

        function safe(label, fn) {
          try { fn(); } catch (e) { console.error("[Brum] " + label + " failed:", e); }
        }
 
        safe("updateCount", function () {
          animateCount(data.found != null ? data.found : 0);
        });
        if (!append) {
          safe("renderFacets",          function () { renderFacets(data); });
          safe("renderHistogram",        function () { renderHistogram(data); });
          safe("syncAllSliderBounds", function () { syncAllSliderBounds(data); });
          safe("renderChips",           function () { renderChips(); });
          safe("updateAllSliderStates", function () { updateAllSliderStates(); });
          safe("updateActiveBadge",     function () { updateActiveBadge(); });
          safe("updateMakeBanner",      function () { updateMakeBanner(); });
          safe("updateMap",             function () { if (typeof brumUpdateMapPins === "function") brumUpdateMapPins(data); });
        }
        if (!append) safe("writeUrl", function () { writeUrl(); });
 
        if (isFacetsOnly) {
          gridStale = true;
        } else {
          gridStale = false;
          safe("renderResults", function () { renderResults(data, append === true); });
        }
      })
      .catch(function (err) {
        console.error("[Brum] search failed", err);
      });
  }
  function scheduleRefresh(delay) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(refresh, delay || 250);
  }

  var SLIDER_FACET_FIELD = {
    year:           "year",
    mileage:        "mileage",
    power:          "power_hp",
    cubic_capacity: "vehicle_details__cubic_capacity",
  };

  function getCardTemplate() {
    var tpl = document.getElementById("brum-card-template");
    if (!tpl || !tpl.content || tpl.content.childElementCount === 0) return null;
    return tpl;
  }

   // tag-slug → "Apple CarPlay" style (best-effort title-casing).
  function prettyTag(s) {
    return String(s || "")
      .replace(/[-_]+/g, " ")
      .replace(/\b([a-z])/g, function (m) { return m.toUpperCase(); });
  }
  
  function localizeCard(card) {
    if (!card || !window.brumTV) return;
    var FIELD = { fuel: 'fuel_type', 'fuel-electric': 'fuel_type', transmission: 'transmission' };
    card.querySelectorAll('[data-vb-icon]').forEach(function (cell) {
      var field = FIELD[cell.getAttribute('data-vb-icon')];
      if (!field) return;
      var s = cell.querySelector('strong'); if (!s) return;
      var t = window.brumTV(field, s.textContent.trim());
      if (t) s.textContent = t;
    });
    card.querySelectorAll('[data-feat]').forEach(function (el) {
      var c = window.brumTV('chip', el.getAttribute('data-feat'));
      if (c) el.textContent = c;
    });
  }

  function renderResults(data, append) {

    var resultsEl = document.querySelector("[data-brum-results]");
    var emptyEl   = document.querySelector("[data-brum-empty]");
    var template  = getCardTemplate();
    if (!resultsEl) return;
    var hits = (data && data.hits) || [];
    if (!append) {
      resultsEl.innerHTML = "";
    }
    if (!append && hits.length === 0) {
      if (emptyEl) emptyEl.hidden = false;
      updatePaginator(0, data.found || 0);
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    var _brumRoot = document.querySelector(".brum");
    if (_brumRoot) _brumRoot.removeAttribute("data-brum-booting");
    if (!template) {

      var frag = document.createDocumentFragment();
      hits.forEach(function (h) {
        var li = document.createElement("li");
        li.innerHTML =
          '<a href="/products/' + encodeURIComponent(h.handle || "") + '" class="brum-card-fallback">' +
          (h.image_url ? '<img src="' + escapeHtml(h.image_url) + '" alt="' + escapeHtml(h.title || "") + '">' : '') +
          '<p>' + escapeHtml(h.title || "") + '</p>' +
          '<p>€' + Number(h.price || 0).toLocaleString('de-DE') + '</p>' +
          '</a>';
        frag.appendChild(li);
      });
      resultsEl.appendChild(frag);
    } else {

      var renderedCount = 0;
      hits.forEach(function (hit, idx) {
        try {
          var clone = template.content.cloneNode(true);
          var li = document.createElement("li");
          li.className = "js-pagination-result";
          while (clone.firstChild) li.appendChild(clone.firstChild);
          try {
            window.BrumCard.apply(li, hit);
          } catch (errData) {
            console.error("[Brum] BrumCard.apply failed for hit " + idx + ":", errData, hit);
          }
          localizeCard(li);
          resultsEl.appendChild(li);
          renderedCount++;
        } catch (errClone) {
          console.error("[Brum] clone failed for hit " + idx + ":", errClone);
        }
      });
    }
    var loaded = resultsEl.children.length;
    var total  = data.found || loaded;
    updatePaginator(loaded, total);
  }
  function updatePaginator(loaded, total) {
    // Paginator is also outside the root [data-brum] wrapper.
    var paginator = document.querySelector("[data-brum-paginator]");
    var statusEl  = document.querySelector("[data-brum-paginator-status]");
    var fillEl    = document.querySelector("[data-brum-paginator-fill]");
    var btn       = document.querySelector("[data-brum-show-more]");
    if (!paginator) return;
    if (total === 0) { paginator.hidden = true; return; }
    paginator.hidden = false;
    if (statusEl) { var Up = window.BRUM_UI || {}; statusEl.textContent = (Up.showing || "Showing") + " " + loaded.toLocaleString('de-DE') + " " + (Up.of || "of") + " " + total.toLocaleString('de-DE'); }
    var pct = total > 0 ? (loaded / total) * 100 : 0;
    if (fillEl) fillEl.style.width = pct + "%";
    if (btn) btn.hidden = loaded >= total;
  }
 
  function hasRealData(items) {
    for (var i = 0; i < items.length; i++) {
      var v = items[i].value;
      if (v !== "" && v != null) return true;
    }
    return false;
  }
  function applyEmptyState(section, facet, hasData) {
    if (hasData) {
      seenFacets[facet] = true;
      section.classList.remove('is-empty');
      section.classList.remove('is-unavailable');
    } else if (seenFacets[facet]) {
      section.classList.add('is-empty');
      section.classList.remove('is-unavailable');
    } else {
      section.classList.add('is-unavailable');
      section.classList.remove('is-empty');
    }
  }
  function translateValue(facet, value) {
    var D = window.BRUM_VALUE_I18N;
    if (!D) return null;
    var L = (document.documentElement.lang || "sl").toLowerCase().slice(0, 2);
    if (D[facet]) {
      var m = D[facet][L] || D[facet].sl;
      if (m && m[value] != null) return m[value];
    }
    if (D.feature) {
      var f = D.feature[L] || D.feature.sl;
      if (f && f[value] != null) return f[value];
    }
    return null;
  }

  function renderFacets(data) {
    // Range slider: shown only when an electric fuel is selected.
    var evSel = state.facets.fuel_type;
    var evActive = !!evSel && (evSel.has("Electric") || evSel.has("Petrol/EV") || evSel.has("Diesel/EV"));
    root.querySelectorAll('[data-ev-only]').forEach(function (el) { el.hidden = !evActive; });
    if (evActive) updateAllSliderStates();
    FACETS.forEach(function (facet) {
      var section = root.querySelector('[data-filter-section="' + facet + '"]');
      if (!section) return;
      var items = (data.facets && data.facets[facet]) || [];
      applyEmptyState(section, facet, hasRealData(items));
    });
    // Sliders: same logic; price is always shown.
    SLIDERS.forEach(function (s) {
      var section = root.querySelector('[data-filter-section="' + s.name + '"]');
      if (!section) return;
      if (s.name === "price" || s.name === "range" || s.name === "battery") { section.classList.remove('is-empty'); section.classList.remove('is-unavailable'); return; }
      var fld = SLIDER_FACET_FIELD[s.name];
      var items = (data.facets && data.facets[fld]) || [];
      applyEmptyState(section, s.name, hasRealData(items));
    });
    FACETS.forEach(function (facet) {
      var wrapper = root.querySelector('[data-options-list="' + facet + '"]');
      if (!wrapper) return;
      var items = (data.facets && data.facets[facet]) || [];
 
      if (greyable(facet) && FACET_UNIVERSE[facet] && FACET_UNIVERSE[facet].length) {
        var cnt = {};
        items.forEach(function (it) { if (it && it.value != null) cnt[it.value] = it.count; });
        items = FACET_UNIVERSE[facet].map(function (v) { return { value: v, count: (cnt[v] != null ? cnt[v] : 0) }; });
      }
      if (facet === "make" || facet === "model") {
        items = items.slice().sort(function (a, b) {
          return String(a.value).localeCompare(String(b.value), undefined, { sensitivity: "base" });
        });
      }
      if (NUMERIC_SORT.has(facet)) {
        items = items.slice().sort(function (a, b) { return Number(a.value) - Number(b.value); });
      }
      if (FACET_VALUE_ORDER[facet]) {
        var ord = FACET_VALUE_ORDER[facet];
        items = items.slice().sort(function (a, b) {
          var ia = ord.indexOf(String(a.value)); if (ia === -1) ia = 999;
          var ib = ord.indexOf(String(b.value)); if (ib === -1) ib = 999;
          return ia - ib;
        });
      }
      items = items.filter(function (it) { return it.value !== "" && it.value != null; });
      var term = (facetSearchTerms[facet] || "").toLowerCase();
      if (term) {
        // Rank matches: whole-string prefix → word-start prefix → contains-anywhere.
        // Keeps the intuitive "type 'm' → M-makes on top" while still surfacing
        // word matches (e.g. Aston Martin) and substring matches last.
        var t1 = [], t2 = [], t3 = [];
        items.forEach(function (it) {
          var v = String(it.value).toLowerCase();
          if (v.indexOf(term) === 0) { t1.push(it); return; }
          var wordPrefix = v.split(/[\s\-]+/).some(function (w) { return w.indexOf(term) === 0; });
          if (wordPrefix) { t2.push(it); return; }
          if (v.indexOf(term) !== -1) t3.push(it);
        });
        items = t1.concat(t2, t3);
      }
      var fsection = wrapper.closest('.brum__filter');
      var hasAvail = items.some(function (it) { return (it.count || 0) > 0; });
      if (HIDE_WHEN_EMPTY.has(facet) && fsection) { fsection.hidden = !hasAvail; }
      var showPills = hasAvail || greyable(facet);
      if (!showPills) {
        wrapper.innerHTML = '<li class="is-empty">—</li>';
      } else {

        var listHtml = items.map(function (it, i) {
          var checked = state.facets[facet].has(it.value);
          var safeValue = escapeHtml(it.value);
          var displayValue = escapeHtml(translateValue(facet, it.value) || (TAG_FACETS.has(facet) ? prettyTag(it.value) : it.value));
          var id = 'brum-f-' + facet + '-' + i;
         
          var iconUrl = BRUM_ICONS[String(it.value).toLowerCase().trim()];
          var _isColorFacet = (facet === "exterior_color" || facet === "vehicle_details__interior_primary_color");
          var _swatchHex = _isColorFacet ? BRUM_COLORS[String(it.value).toLowerCase()] : null;
          var iconClass = iconUrl ? ' has-icon' : (_swatchHex ? ' has-swatch' : '');
          var iconStyle = iconUrl ? ' style="--brum-facet-icon: url(\'' + iconUrl + '\')"' : (_swatchHex ? ' style="--brum-facet-swatch: ' + _swatchHex + '"' : '');
          if (facet === "make") {
            return '<li class="brum__facet-item brum__facet-item--tap' + iconClass + (checked ? ' is-selected' : '') + '"' + iconStyle
              +   ' data-tap-value="' + safeValue + '" data-tap-facet="make" role="button" tabindex="0">'
              +   '<span class="label">' + displayValue + '</span>'
              +   '<span class="count">' + (it.count != null ? it.count : "") + '</span>'
              + '</li>';
          }
          var unavail = (it.count === 0);
          return '<li class="brum__facet-item' + iconClass + (unavail ? ' is-unavailable' : '') + '"' + iconStyle + '>'
              +   '<input type="checkbox"'
              +     ' id="' + id + '"'
              +     ' value="' + safeValue + '"'
              +     ' data-facet-value="' + safeValue + '"'
              +     (checked ? " checked" : "") + (unavail ? " disabled" : "") + '>'
              +   '<label for="' + id + '">'
              +     '<span class="label">' + displayValue + '</span>'
              +     '<span class="count">' + (it.count != null ? it.count : "") + '</span>'
              +   '</label>'
              + '</li>';
        }).join("");

        if (facet === "model" && state.mmFocusMake) {
          var fm = escapeHtml(state.mmFocusMake);
          var makeRaw = state.mmFocusMake;
          // render one model checkbox <li>
          function modelLi(it, i) {
            var ck = state.facets.model.has(it.value);
            var sv = escapeHtml(it.value);
            var mid = 'brum-f-model-' + i;
            return '<li class="brum__facet-item">'
              +   '<input type="checkbox" id="' + mid + '" value="' + sv + '" data-facet-value="' + sv + '"' + (ck ? ' checked' : '') + '>'
              +   '<label for="' + mid + '"><span class="label">' + sv + '</span>'
              +     '<span class="count">' + (it.count != null ? it.count : "") + '</span></label>'
              + '</li>';
          }
          // group items by derived series
          var grp = {}, order = [], ungrouped = [];
          items.forEach(function (it, i) {
            var ser = modelSeries(makeRaw, it.value);
            if (!ser) { ungrouped.push({ it: it, i: i }); return; }
            if (!grp[ser]) { grp[ser] = []; order.push(ser); }
            grp[ser].push({ it: it, i: i });
          });
          // sum member model counts → umbrella count (each car has one model, no double-count)
          function sumCounts(list) {
            return list.reduce(function (a, x) {
              var c = x.it ? x.it.count : x.count;
              return a + (c != null ? c : 0);
            }, 0);
          }
          // make-level "View all" — count = all models for this make
          var makeTotal = sumCounts(items);
          var viewAllChecked = state.viewAllMakes.has(state.mmFocusMake);
          var built = '<li class="brum__facet-item brum__facet-item--view-all">'
              +   '<input type="checkbox" id="brum-view-all-model" data-view-all="' + fm + '"' + (viewAllChecked ? ' checked' : '') + '>'
              +   '<label for="brum-view-all-model"><span class="label">' + ((window.BRUM_UI || {}).viewAll || "View all") + ' ' + fm + '</span>'
              +     '<span class="count">' + makeTotal + '</span></label>'
              + '</li>';
          // per-series umbrella + its models
          order.forEach(function (ser, gi) {
            var serSafe = escapeHtml(ser);
            var serChecked = state.seriesMake[ser] === state.mmFocusMake;
            var serTotal = sumCounts(grp[ser]);
            built += '<li class="brum__facet-item brum__facet-item--view-all brum__facet-item--series">'
                +   '<input type="checkbox" id="brum-view-all-series-' + gi + '" data-view-all-series="' + serSafe + '"' + (serChecked ? ' checked' : '') + '>'
                +   '<label for="brum-view-all-series-' + gi + '"><span class="label">' + ((window.BRUM_UI || {}).viewAll || "View all") + ' ' + serSafe + '</span>'
                +     '<span class="count">' + serTotal + '</span></label>'
                + '</li>';
            built += grp[ser].map(function (g) { return modelLi(g.it, g.i); }).join("");
          });
          // standalone models (no series) last
          built += ungrouped.map(function (g) { return modelLi(g.it, g.i); }).join("");
          listHtml = built;
        }
        wrapper.innerHTML = listHtml;
      }

      var sublistEl = wrapper.closest(".brum__sublist");
      if (sublistEl) sublistEl.classList.remove("is-loading");
      var section = root.querySelector('[data-filter-section="' + facet + '"]');
      var chip = section && section.querySelector('[data-summary-chip]');
      if (chip) {
        var active = Array.from(state.facets[facet]);
        chip.textContent = active.length === 0 ? "" :
          (active.length === 1 ? active[0] : active.length + " " + ((window.BRUM_UI || {}).selected || "selected"));
      }
    });
    // Grouped panels: aggregate the chip from member facets.
    Object.keys(FACET_GROUPS).forEach(function (gid) {
      var groupEl = root.querySelector('[data-filter-section="' + gid + '"]');
      if (!groupEl) return;
      var gchip = groupEl.querySelector('[data-summary-chip]');
      if (!gchip) return;
      var n = 0;
      FACET_GROUPS[gid].forEach(function (f) { n += (state.facets[f] ? state.facets[f].size : 0); });
      gchip.textContent = n === 0 ? "" : n + " " + ((window.BRUM_UI || {}).selected || "selected");
    });

    var mmSection = root.querySelector('[data-filter-section="make"]');
    var mmChip = mmSection && mmSection.querySelector('[data-summary-chip]');
    if (mmChip) {
      var makes = Array.from(state.facets.make);
      var models = Array.from(state.facets.model);
 
      var modelToMake = {};
      (data.hits || []).forEach(function (h) {
        if (h && h.model && h.make && !modelToMake[h.model]) {
          modelToMake[h.model] = h.make;
        }
      });
      function pairFor(mod) {
        return state.modelMake[mod] || modelToMake[mod];
      }
      // Group: which models belong to each picked make.
      var modelsByMake = {};
      makes.forEach(function (m) { modelsByMake[m] = []; });
      models.forEach(function (mod) {
        var mk = pairFor(mod);
        if (mk) {
          if (!modelsByMake[mk]) modelsByMake[mk] = [];
          modelsByMake[mk].push(mod);
        }
      });
      // Series umbrellas ("1 Series") show in the chip text alongside models.
      Object.keys(state.seriesMake).forEach(function (ser) {
        var mk = state.seriesMake[ser];
        if (!modelsByMake[mk]) modelsByMake[mk] = [];
        modelsByMake[mk].push(ser);
      });
      var html = "";

      function chipHtml(make, modelsText) {
        var logo = BRUM_ICONS[String(make).toLowerCase()] || "";
        var styleAttr = logo ? ' style="background-image:url(\'' + logo + '\')"' : "";
        var text = modelsText
          ? '<span class="brum__mm-chip-text">' + escapeHtml(modelsText) + '</span>'
          : "";
        return '<span class="brum__mm-chip" data-make="' + escapeHtml(make) + '">'
          +   '<span class="brum__mm-chip-logo"' + styleAttr + '></span>'
          +   text
          +   '<button type="button" class="brum__mm-chip-remove" aria-label="Remove"'
          +       ' data-mm-chip-remove="make"'
          +       ' data-chip-make="' + escapeHtml(make) + '">×</button>'
          + '</span>';
      }
      makes.forEach(function (mk) {
        var ms = modelsByMake[mk] || [];
        html += chipHtml(mk, ms.length === 0 ? null : ms.join(", "));
      });
      mmChip.innerHTML = html;

      var section = root.querySelector(".brum__filter--make-model");
      if (section) section.classList.toggle("has-selections", makes.length > 0 || models.length > 0);
      var drawerForDone = root.querySelector("[data-drawer]");
      if (drawerForDone) {
        var mmHasSel = state.facets.model.size > 0 || Object.keys(state.seriesMake).length > 0 || state.viewAllMakes.size > 0;
        drawerForDone.setAttribute("data-mm-has-selection", mmHasSel ? "true" : "false");
      }
    }
  }
  // -------- Render: chips --------
  function renderChips() {
    var el = root.querySelector("[data-chips]"); if (!el) return;
    var chips = [];
    FACETS.forEach(function (facet) {
      state.facets[facet].forEach(function (value) {
        chips.push({ kind:"facet", facet:facet, value:value, label:value });
      });
    });
    SLIDERS.forEach(function (s) {
      var lo = state.range[s.minKey], hi = state.range[s.maxKey];
      if (lo !== "" || hi !== "") {
        var loTxt = (lo !== "" ? s.fmt(lo) : "any");
        var hiTxt = (hi !== "" ? s.fmt(hi) : "any");
        chips.push({ kind:"range", minKey:s.minKey, maxKey:s.maxKey, label:loTxt + " – " + hiTxt });
      }
    });
    if (chips.length === 0) { el.innerHTML = ""; return; }
    el.innerHTML = chips.map(function (c, i) {
      return '<span class="brum__chip">' + escapeHtml(c.label)
           + '<button type="button" data-chip-remove="' + i + '" aria-label="Remove filter">×</button></span>';
    }).join("") + '<button type="button" class="brum__chips-clear" data-chips-clear>' + ((window.BRUM_UI || {}).clearAll || "Clear all") + '</button>';
    el.__chips = chips;
  }

function renderHistogram(data) {
    var el = root.querySelector("[data-histogram]"); if (!el) return;
    var bucketFacet = (data.facets && data.facets.price_bucket) || [];
    if (bucketFacet.length === 0) { el.innerHTML = ""; return; }
    var entries = bucketFacet
      .map(function (f) { return { bucket: Number(f.value), count: f.count }; })
      .filter(function (e) { return Number.isFinite(e.bucket); });
    if (entries.length === 0) { el.innerHTML = ""; return; }
    var dataMinEur = Math.min.apply(null, entries.map(function (e) { return e.bucket; })) * 1000;
    var dataMaxEur = (Math.max.apply(null, entries.map(function (e) { return e.bucket; })) + 1) * 1000;
    if (dataMaxEur <= dataMinEur) dataMaxEur = dataMinEur + 1000;
    var priceUserMin = state.range.price_min;
    var priceUserMax = state.range.price_max;
    var priceUserTouched = (priceUserMin !== undefined && priceUserMin !== "") ||
                           (priceUserMax !== undefined && priceUserMax !== "");
    if (!priceUserTouched && (dataMinEur !== BOUNDS.price.min || dataMaxEur !== BOUNDS.price.max)) {
      BOUNDS.price.min = dataMinEur;
      BOUNDS.price.max = dataMaxEur;
      var pSlider = root.querySelector('[data-slider="price"]');
      if (pSlider) {
        pSlider.querySelectorAll("[data-slider-thumb]").forEach(function (th) {
          th.min = dataMinEur;
          th.max = dataMaxEur;
        });
        updateAllSliderStates();
      }
    }
    var BINS = 120;
    var bins = new Array(BINS).fill(0);
    entries.forEach(function (e) {
      var priceMid = e.bucket * 1000 + 500;
      var idx = Math.min(BINS - 1, Math.max(0, Math.floor(((priceMid - dataMinEur) / (dataMaxEur - dataMinEur)) * BINS)));
      bins[idx] += e.count;
    });
    var peak = Math.max.apply(null, bins);
    var logPeak = peak > 0 ? Math.log1p(peak) : 1;
    var W = 1000;
    var H = 100;
    var barSlot = W / BINS;
    var barW = barSlot * 0.5;

    // Reuse existing SVG/rects if they exist, otherwise create
    var svg = el.querySelector("svg");
    if (!svg || svg.querySelectorAll("rect").length !== BINS) {
      var rects = bins.map(function (count, i) {
        var x = i * barSlot + (barSlot - barW) / 2;
        return '<rect x="' + x + '" y="' + H + '" width="' + barW + '" height="0"/>';
      }).join("");
      el.innerHTML = '<svg width="100%" height="80" viewBox="0 0 ' + W + ' ' + H + '" preserveAspectRatio="none">' + rects + '</svg>';
      svg = el.querySelector("svg");
    }

    var rectEls = svg.querySelectorAll("rect");
    bins.forEach(function (count, i) {
      var binStart = dataMinEur + (i / BINS) * (dataMaxEur - dataMinEur);
      var binEnd   = dataMinEur + ((i + 1) / BINS) * (dataMaxEur - dataMinEur);
      var hPct = count === 0 ? 2 : Math.max(4, (Math.log1p(count) / logPeak) * 90);
      var y = H - hPct;
      var r = rectEls[i];
      r.setAttribute("y", y);
      r.setAttribute("height", hPct);
      r.setAttribute("data-bar-start", binStart);
      r.setAttribute("data-bar-end", binEnd);
    });
    updateHistogramPriceRange();
  }
 
function updateHistogramPriceRange() {
    var el = root.querySelector("[data-histogram]");
    if (!el) return;
    var bars = el.querySelectorAll("rect");
    if (!bars.length) return;
    var selLoRaw = Number(state.range.price_min);
    var selHiRaw = Number(state.range.price_max);
    var selLo = Number.isFinite(selLoRaw) && state.range.price_min !== "" ? selLoRaw : -Infinity;
    var selHi = Number.isFinite(selHiRaw) && state.range.price_max !== "" ? selHiRaw :  Infinity;
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var bStart = Number(bar.dataset.barStart);
      var bEnd   = Number(bar.dataset.barEnd);
      var inRange = bEnd >= selLo && bStart <= selHi;
      bar.classList.toggle("in", inRange);
    }
  }

  function syncSliderBounds(data, sliderName, facetField, bucket) {
    var facet = (data.facets && data.facets[facetField]) || [];
    if (facet.length === 0) return;
    var sliderCfg = SLIDERS.filter(function (x) { return x.name === sliderName; })[0];
    var userMin = state.range[sliderCfg ? sliderCfg.minKey : sliderName + "_min"];
    var userMax = state.range[sliderCfg ? sliderCfg.maxKey : sliderName + "_max"];
    if ((userMin !== undefined && userMin !== "") || (userMax !== undefined && userMax !== "")) {
      return;
    }
    var values = facet
      .map(function (f) { return Number(f.value); })
      .filter(function (n) { return Number.isFinite(n); });
    if (values.length === 0) return;
    // Discrete slider: remember the exact values present so the thumb snaps to them.
    if (DISCRETE_SLIDERS[sliderName]) SLIDER_VALUES[sliderName] = values.slice().sort(function (a, b) { return a - b; });
    var rawMin = Math.min.apply(null, values);
    var rawMax = Math.max.apply(null, values);
    var dataMin = bucket ? rawMin * bucket : rawMin;
    var dataMax = bucket ? (rawMax + 1) * bucket : rawMax;
    if (dataMin === dataMax) dataMax = dataMin + (bucket || 1);
    // Snap bounds to the slider's step grid so the max thumb can land exactly
    // on the bound. A non-aligned max (e.g. 6498 with step 100) gets sanitized
    // by the browser down to the last valid step (6400), parking the thumb short
    // of the right edge. Rounding the bound to 6500 fixes both the position and
    // the residual-ceiling filter.
    var stepEl = root.querySelector('[data-slider="' + sliderName + '"]');
    var gridStep = stepEl ? (parseFloat(stepEl.dataset.step) || 1) : 1;
    if (gridStep > 1) {
      dataMin = Math.floor(dataMin / gridStep) * gridStep;
      dataMax = Math.ceil(dataMax / gridStep) * gridStep;
    }
    if (dataMin !== BOUNDS[sliderName].min || dataMax !== BOUNDS[sliderName].max) {
      BOUNDS[sliderName].min = dataMin;
      BOUNDS[sliderName].max = dataMax;
      var slider = root.querySelector('[data-slider="' + sliderName + '"]');
      if (slider) {
        slider.querySelectorAll("[data-slider-thumb]").forEach(function (th) {
          th.min = dataMin;
          th.max = dataMax;
        });
        updateAllSliderStates();
      }
    }
  }

  function syncAllSliderBounds(data) {
    [
      { slider: "year",    facet: "year" },
      { slider: "mileage", facet: "mileage" },
      { slider: "power",   facet: "power_hp" },
      { slider: "price",   facet: "price_bucket", bucket: 1000 },
      { slider: "cubic_capacity", facet: "vehicle_details__cubic_capacity" }
    ].forEach(function (m) {
      syncSliderBounds(data, m.slider, m.facet, m.bucket);
    });
  }
  // -------- Active filter badge --------
  function countActive() {
    var n = 0;
    FACETS.forEach(function (f) { n += state.facets[f].size; });
    SLIDERS.forEach(function (s) {
      if (state.range[s.minKey] !== "" || state.range[s.maxKey] !== "") n += 1;
    });
    return n;
  }
  function updateActiveBadge() {
    var n = countActive();
    var b = root.querySelector("[data-active-count]");
    if (!b) return;
    if (n > 0) { b.textContent = n; b.hidden = false; } else { b.hidden = true; }
  }
  // -------- Sliders --------
  function setupSliders() {
  SLIDERS.forEach(function (s) {
    var sliderEl = root.querySelector('[data-slider="' + s.name + '"]'); if (!sliderEl) return;
    var minThumb = sliderEl.querySelector('[data-slider-thumb="min"]');
    var maxThumb = sliderEl.querySelector('[data-slider-thumb="max"]');
    var step = parseFloat(sliderEl.dataset.step) || 1;
    var b = BOUNDS[s.name];
    minThumb.min = b.min; minThumb.max = b.max; minThumb.step = step;
    maxThumb.min = b.min; maxThumb.max = b.max; maxThumb.step = step;
    minThumb.value = b.min; maxThumb.value = b.max;
    function onInput() {
      if (DISCRETE_SLIDERS[s.name]) {
        var dlo = snapToValues(s.name, parseFloat(minThumb.value));
        var dhi = snapToValues(s.name, parseFloat(maxThumb.value));
        if (dlo > dhi) { if (this === minThumb) dhi = dlo; else dlo = dhi; }
        minThumb.value = dlo; maxThumb.value = dhi;
        renderSliderFill(s.name);
        // Always show the real snapped value (incl. the extremes like 1 and 5)
        // so every value is visible; "edge = no filter" is applied in onChange.
        setRangeInput(s.minKey, String(dlo));
        setRangeInput(s.maxKey, String(dhi));
        return;
      }
      var lo = parseFloat(minThumb.value), hi = parseFloat(maxThumb.value);
      var which = this === minThumb ? "min" : "max";
      if (lo > hi - step) {
        if (this === minThumb) minThumb.value = hi - step;
        else maxThumb.value = lo + step;
        lo = parseFloat(minThumb.value); hi = parseFloat(maxThumb.value);
      }
      // Treat the thumb as unbounded when within one step of the edge — the
      // thumb snaps to step increments, so a non-step-aligned bound (e.g. a
      // max mileage of 610,289 with step 1,000) can never be hit exactly.
      // Strict equality there would leave a ceiling filter and drop the
      // top results even with the slider dragged fully right.
      var atMin = lo - step < b.min;
      var atMax = hi + step > b.max;
      renderSliderFill(s.name);
      setRangeInput(s.minKey, atMin ? "" : String(lo));
      setRangeInput(s.maxKey, atMax ? "" : String(hi));
      if (s.name === "price") {
        state.range[s.minKey] = atMin ? "" : String(lo);
        state.range[s.maxKey] = atMax ? "" : String(hi);
        updateHistogramPriceRange();
      }
    }
    function onChange() {
      if (DISCRETE_SLIDERS[s.name]) {
        var dv = SLIDER_VALUES[s.name] || [];
        var dlo = snapToValues(s.name, parseFloat(minThumb.value));
        var dhi = snapToValues(s.name, parseFloat(maxThumb.value));
        state.range[s.minKey] = (dv.length && dlo <= dv[0]) ? "" : String(dlo);
        state.range[s.maxKey] = (dv.length && dhi >= dv[dv.length - 1]) ? "" : String(dhi);
        state.page = 1;
        refresh();
        return;
      }
      var lo = parseFloat(minThumb.value), hi = parseFloat(maxThumb.value);
      // Within one step of the edge = unbounded (see onInput note).
      state.range[s.minKey] = (lo - step < b.min) ? "" : String(lo);
      state.range[s.maxKey] = (hi + step > b.max) ? "" : String(hi);
      state.page = 1;
      refresh();
    }
    minThumb.addEventListener("input", onInput);
    maxThumb.addEventListener("input", onInput);
    minThumb.addEventListener("change", onChange);
    maxThumb.addEventListener("change", onChange);
    renderSliderFill(s.name);
  });
}
    function setRangeInput(key, val) {
    var el = root.querySelector('[data-range="' + key + '"]');
    if (!el) return;
    if (key === "power_min" || key === "power_max") {
      el.value = val === "" ? "" : String(displayPower(Number(val)));
      return;
    }
    var formatWithCommas = (key === "price_min" || key === "price_max" ||
                            key === "mileage_min" || key === "mileage_max");
    if (formatWithCommas && val !== "") {
      el.value = Number(val).toLocaleString('de-DE');
    } else {
      el.value = val;
    }
  }
  function renderSliderFill(name) {
    var sliderEl = root.querySelector('[data-slider="' + name + '"]'); if (!sliderEl) return;
    var minThumb = sliderEl.querySelector('[data-slider-thumb="min"]');
    var maxThumb = sliderEl.querySelector('[data-slider-thumb="max"]');
    var fill = sliderEl.querySelector('[data-slider-fill]');
    var b = BOUNDS[name];
    var lo = parseFloat(minThumb.value), hi = parseFloat(maxThumb.value);
    var lowPct = ((lo - b.min) / (b.max - b.min)) * 100;
    var hiPct  = ((hi - b.min) / (b.max - b.min)) * 100;
    fill.style.clipPath = "inset(0 " + (100 - hiPct) + "% 0 " + lowPct + "%)";

    if (lowPct > 50 && hiPct > 50) {
      minThumb.style.zIndex = 2;
      maxThumb.style.zIndex = 1;
    } else if (lowPct < 50 && hiPct < 50) {
      minThumb.style.zIndex = 1;
      maxThumb.style.zIndex = 2;
    } else {
      minThumb.style.zIndex = 2;
      maxThumb.style.zIndex = 1;
    }
  }
  function updateAllSliderStates() {
    SLIDERS.forEach(function (s) {
      var sliderEl = root.querySelector('[data-slider="' + s.name + '"]'); if (!sliderEl) return;
      var minThumb = sliderEl.querySelector('[data-slider-thumb="min"]');
      var maxThumb = sliderEl.querySelector('[data-slider-thumb="max"]');
      var b = BOUNDS[s.name];
      var lo = state.range[s.minKey] === "" ? b.min : clamp(parseFloat(state.range[s.minKey]), b.min, b.max);
      var hi = state.range[s.maxKey] === "" ? b.max : clamp(parseFloat(state.range[s.maxKey]), b.min, b.max);
      minThumb.value = lo; maxThumb.value = hi;
      renderSliderFill(s.name);
      var section = root.querySelector('[data-filter-section="' + s.name + '"]');
      var chip = section && section.querySelector('[data-summary-chip]');
      if (chip) {

        var isInline = section && section.classList.contains("brum__filter--inline");
        if (isInline) {
          chip.textContent = "";
        } else {

          var hasMin = state.range[s.minKey] !== "" && Number(state.range[s.minKey]) > b.min;
          var hasMax = state.range[s.maxKey] !== "" && Number(state.range[s.maxKey]) < b.max;
          if (hasMin && hasMax) {
            chip.textContent = s.fmt(lo) + " – " + s.fmt(hi);
          } else {
            chip.textContent = "";
          }
        }
      }
    });
  }
  // -------- Drawer --------
  function openDrawer() {
    var d = root.querySelector("[data-drawer]");
    var ov = root.querySelector("[data-overlay]");
    d.classList.add("is-open"); d.setAttribute("aria-hidden", "false");
    ov.hidden = false; requestAnimationFrame(function () { ov.classList.add("is-visible"); });
    // Keep the products-toolbar filter button's aria/visual state in sync.
    document.querySelectorAll(".js-toggle-filters").forEach(function (b) {
      b.setAttribute("aria-expanded", "true");
      b.classList.add("is-active");
    });
    var brumScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.dataset.brumScrollLock = String(brumScrollY);
    document.body.style.position = "fixed";
    document.body.style.top = (-brumScrollY) + "px";
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
  }
  function closeDrawer() {
    var d = root.querySelector("[data-drawer]");
    var ov = root.querySelector("[data-overlay]");
    d.classList.remove("is-open"); d.setAttribute("aria-hidden", "true");
    ov.classList.remove("is-visible");
    setTimeout(function () { if (!ov.classList.contains("is-visible")) ov.hidden = true; }, 250);
    document.querySelectorAll(".js-toggle-filters").forEach(function (b) {
      b.setAttribute("aria-expanded", "false");
      b.classList.remove("is-active");
    });
    // Reset to list mode so the next open starts at the top level.
    d.setAttribute("data-mode", "list");
    d.removeAttribute("data-mm-active");
    state.mmFocusMake = null;
    root.querySelectorAll(".brum__filter").forEach(function (f) {
      f.classList.remove("is-active");
      // Keep inline filters (Year) always open.
      if (f.classList.contains("brum__filter--inline")) return;
      f.removeAttribute("open");
    });
    var brumScrollY = parseInt(document.body.dataset.brumScrollLock || "0", 10);
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    document.body.style.overflow = "";
    delete document.body.dataset.brumScrollLock;
    window.scrollTo(0, brumScrollY);

    if (gridStale) {
      gridStale = false;
      refresh();
    }
  }
  // -------- Events --------

  document.addEventListener("click", function (e) {
    var btn = e.target.closest && e.target.closest(".js-toggle-filters");
    if (!btn) return;
    e.preventDefault();
    openDrawer();
  });
  root.querySelector("[data-close-drawer]").addEventListener("click", closeDrawer);
  root.querySelector("[data-overlay]").addEventListener("click", closeDrawer);
  root.querySelector("[data-apply]").addEventListener("click", closeDrawer);
  if (location.hash === "#filter") {
    setTimeout(openDrawer, 0);
    try { history.replaceState(null, "", location.pathname + location.search); } catch (err) {}
  }

  function enterFilterDetail(filterName) {
    var drawer = root.querySelector("[data-drawer]");
    if (!drawer) return;
    var bodyBefore = root.querySelector(".brum__drawer-body");
    if (bodyBefore) {
      drawer.dataset.preDrillScroll = String(bodyBefore.scrollTop);
    }
    root.querySelectorAll(".brum__filter").forEach(function (f) {
      if (f.classList.contains("brum__filter--inline")) return;
      var isActive = f.dataset.filterSection === filterName;
      f.classList.toggle("is-active", isActive);
      if (isActive) f.setAttribute("open", "");
      else          f.removeAttribute("open");
    });
    var mmSection = root.querySelector('[data-filter-section="make"]');
    if (filterName === "make" && mmSection) {
      mmSection.dataset.mmStep = "makes";
      state.mmFocusMake = null;
      drawer.setAttribute("data-mm-active", "true");
    } else {
      drawer.removeAttribute("data-mm-active");
    }
    var titleEl = root.querySelector("[data-detail-title]");
    if (titleEl) {
      var title;
      if (filterName === "make") {
        title = (window.BRUM_UI || {}).make || "Make";
      } else {
        var activeSection = root.querySelector('[data-filter-section="' + filterName + '"]');
        var labelEl = activeSection && activeSection.querySelector('.brum__filter-label');
        title = labelEl ? labelEl.textContent : filterName.replace(/_/g, " ");
      }
      titleEl.textContent = title;
    }
    drawer.setAttribute("data-mode", "detail");
    var body = root.querySelector(".brum__drawer-body");
    if (body) body.scrollTop = 0;
    root.querySelectorAll(".brum__facet-search.is-hidden").forEach(function (s) {
      s.classList.remove("is-hidden");
    });
  }
function exitFilterDetail() {
    var drawer = root.querySelector("[data-drawer]");
    if (!drawer) return;
    drawer.setAttribute("data-mode", "list");
    drawer.removeAttribute("data-mm-active");
    state.mmFocusMake = null;
    root.querySelectorAll(".brum__filter.is-active").forEach(function (f) {
      f.classList.remove("is-active");
      if (f.classList.contains("brum__filter--inline")) return;
      f.removeAttribute("open");
    });
    var body = root.querySelector(".brum__drawer-body");
    if (body) {
      var saved = parseInt(drawer.dataset.preDrillScroll || "0", 10);
      var target = isFinite(saved) && saved > 0 ? saved : 0;
      body.scrollTop = target;
      requestAnimationFrame(function () {
        body.scrollTop = target;
      });
    }
  }
  root.querySelectorAll(".brum__filter > .brum__filter-summary").forEach(function (s) {
    s.addEventListener("click", function (e) {
      e.preventDefault();
      if (e.target.closest && e.target.closest("[data-filter-clear]")) return;
      var removeBtn = e.target.closest && e.target.closest("[data-mm-chip-remove]");
      if (removeBtn) {
        e.stopPropagation();
        var make = removeBtn.dataset.chipMake;
        state.viewAllMakes.delete(make);
        Object.keys(state.modelMake).forEach(function (mod) {
          if (state.modelMake[mod] === make) {
            state.facets.model.delete(mod);
            delete state.modelMake[mod];
          }
        });
        Object.keys(state.seriesMake).forEach(function (ser) {
          if (state.seriesMake[ser] === make) delete state.seriesMake[ser];
        });
        syncFacetsMake();
        state.page = 1;
        refresh();
        return;
      }

      var chipEl = e.target.closest && e.target.closest(".brum__mm-chip");
      if (chipEl) {
        e.stopPropagation();
        var chipMake = chipEl.dataset.make;
        if (chipMake) {
          enterFilterDetail("make");
          var mmSec = root.querySelector('[data-filter-section="make"]');
          if (mmSec) {
            mmSec.dataset.mmStep = "models";
            state.mmFocusMake = chipMake;
            var drawer = root.querySelector("[data-drawer]");
            if (drawer) drawer.setAttribute("data-mm-active", "true");
            var titleEl = root.querySelector("[data-detail-title]");
            (window.BRUM_UI || {}).models || "Models"
            var modelSublist = root.querySelector(".brum__sublist--model");
            if (modelSublist) modelSublist.classList.add("is-loading");
          }
          state.page = 1;
          refresh();
        }
        return;
      }
      var filter = s.parentElement;
      // Inline filters (Year) don't drill down; they're already expanded.
      if (filter.classList.contains("brum__filter--inline")) return;
      var section = filter && filter.dataset.filterSection;
      if (section) enterFilterDetail(section);
    });
  });
  var backBtn = root.querySelector("[data-back-to-list]");
  if (backBtn) backBtn.addEventListener("click", exitFilterDetail);

  var mmDoneBtn = root.querySelector("[data-mm-done]");
  if (mmDoneBtn) mmDoneBtn.addEventListener("click", exitFilterDetail);

  var drawerBody = root.querySelector(".brum__drawer-body");
  if (drawerBody) {
    var brumLastScrollTop = 0;
    drawerBody.addEventListener("scroll", function () {
      var search = root.querySelector(".brum__filter.is-active .brum__facet-search");
      if (!search) return;
      var current = drawerBody.scrollTop;
      var delta = current - brumLastScrollTop;
      if (Math.abs(delta) < 4) return; // ignore micro scrolls
      if (delta > 0 && current > 40) {
        search.classList.add("is-hidden");
      } else if (delta < 0) {
        search.classList.remove("is-hidden");
      }
      brumLastScrollTop = current;
    }, { passive: true });
  }
  // Native-style: dismiss the keyboard when the user drags the list.
  drawerBody.addEventListener("touchmove", function () {
    var a = document.activeElement;
    if (a && a.classList && a.classList.contains("brum__facet-search")) a.blur();
  }, { passive: true });
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") {
      var d = root.querySelector("[data-drawer]");
      if (d && d.classList.contains("is-open")) closeDrawer();
    }
  });
  root.querySelector(".brum__drawer-body").addEventListener("change", function (e) {
    var t = e.target;


    if (t.matches("[data-view-all-series]")) {
      // Umbrella ("View all 1 Series"): filter server-side by the indexed
      // `series` field via series_make=<series>:<make>. No client-side
      // enumeration of model values — reliable + scalable.
      var ser = t.dataset.viewAllSeries;
      var mkFocus = state.mmFocusMake;
      if (t.checked) {
        state.seriesMake[ser] = mkFocus;
        state.viewAllMakes.delete(mkFocus);
      } else {
        delete state.seriesMake[ser];
      }
      syncFacetsMake();
      state.page = 1;
      refresh();
      return;
    }
    if (t.matches("[data-view-all]")) {
      var mk = t.dataset.viewAll;
      if (t.checked) {
        state.viewAllMakes.add(mk);
        Object.keys(state.modelMake).forEach(function (mod) {
          if (state.modelMake[mod] === mk) {
            state.facets.model.delete(mod);
            delete state.modelMake[mod];
          }
        });
      } else {
        state.viewAllMakes.delete(mk);
      }
      syncFacetsMake();
      state.page = 1;
      refresh();
      return;
    }
    if (t.matches("[data-facet-value]")) {
      var listEl = t.closest("[data-options-list]");
      var facet = listEl.dataset.optionsList;
      var value = t.dataset.facetValue;
      if (t.checked) state.facets[facet].add(value); else state.facets[facet].delete(value);

      if (facet === "model") {
        if (t.checked && state.mmFocusMake) {
          state.modelMake[value] = state.mmFocusMake;

          state.viewAllMakes.delete(state.mmFocusMake);
        } else if (!t.checked) {
          delete state.modelMake[value];
        }
        syncFacetsMake();
      }

      if (facet === "make" && !t.checked) {
        Object.keys(state.modelMake).forEach(function (mod) {
          if (state.modelMake[mod] === value) {
            state.facets.model.delete(mod);
            delete state.modelMake[mod];
          }
        });
        Object.keys(state.seriesMake).forEach(function (ser) {
          if (state.seriesMake[ser] === value) delete state.seriesMake[ser];
        });
        state.viewAllMakes.delete(value);
        syncFacetsMake();
      }
      state.page = 1;

      if (facet === "make" && t.checked) {
        var mmSec = root.querySelector('[data-filter-section="make"]');
        if (mmSec && mmSec.dataset.mmStep !== "models") {
          mmSec.dataset.mmStep = "models";

          state.mmFocusMake = value;
          var titleEl2 = root.querySelector("[data-detail-title]");
          if (titleEl2) titleEl2.textContent = (window.BRUM_UI || {}).models || "Models";

          var modelSublist = root.querySelector(".brum__sublist--model");
          if (modelSublist) modelSublist.classList.add("is-loading");
          var body = root.querySelector(".brum__drawer-body");
          if (body) body.scrollTop = 0;

          root.querySelectorAll(".brum__facet-search.is-hidden").forEach(function (s) {
            s.classList.remove("is-hidden");
          });
        }
      }

      refresh();
    } else if (t.matches("[data-range]")) {
      var key = t.dataset.range;
      var raw = String(t.value || "").replace(/[^\d-]/g, "");
      if (key === "power_min" || key === "power_max") {
        raw = raw !== "" ? String(parseUserPower(raw)) : "";
      }
      state.range[key] = raw;
      var formatWithCommas = (key === "price_min" || key === "price_max" ||
                              key === "mileage_min" || key === "mileage_max");
      if (formatWithCommas && raw !== "") {
        t.value = Number(raw).toLocaleString('de-DE');
      }
      state.page = 1;
      scheduleRefresh(350);
    }
  });

  root.querySelector(".brum__drawer-body").addEventListener("click", function (e) {
    var row = e.target.closest && e.target.closest("[data-tap-value][data-tap-facet=\"make\"]");
    if (!row) return;
    var value = row.dataset.tapValue;
    if (!value) return;
    var mmSec = root.querySelector('[data-filter-section="make"]');
    if (mmSec) {
      mmSec.dataset.mmStep = "models";
      state.mmFocusMake = value;
      var titleEl = root.querySelector("[data-detail-title]");
      if (titleEl) titleEl.textContent = (window.BRUM_UI || {}).models || "Models";
      var modelSublist = root.querySelector(".brum__sublist--model");
      if (modelSublist) modelSublist.classList.add("is-loading");
      var body = root.querySelector(".brum__drawer-body");
      if (body) body.scrollTop = 0;
      root.querySelectorAll(".brum__facet-search.is-hidden").forEach(function (s) {
        s.classList.remove("is-hidden");
      });
    }
    state.page = 1;
    refresh();
  });
  root.querySelector(".brum__drawer-body").addEventListener("input", function (e) {
    var t = e.target;
    if (t.matches("[data-facet-search]")) {
      facetSearchTerms[t.dataset.facetSearch] = t.value;
      if (lastData) renderFacets(lastData);
    } else if (t.matches("[data-range]")) {
      var key = t.dataset.range;
      var formatWithCommas = (key === "price_min" || key === "price_max" ||
                              key === "mileage_min" || key === "mileage_max");
      if (formatWithCommas) {
        var raw = String(t.value || "").replace(/[^\d]/g, "");
        var cursorPos = t.selectionStart;
        var oldLength = t.value.length;
        if (raw !== "") {
          t.value = Number(raw).toLocaleString('de-DE');
        }
        var newLength = t.value.length;
        var newPos = cursorPos + (newLength - oldLength);
        try { t.setSelectionRange(newPos, newPos); } catch (err) {}
        // Update state live so the histogram fade reflects typed values
        state.range[key] = raw;
        if (key === "price_min" || key === "price_max") {
          updateHistogramPriceRange();
        }
      }
    }
  });

  var SHOPIFY_TO_BRUM_SORT = {
    "manual":              "created_at:desc",
    "best-selling":        "created_at:desc",
    "title-ascending":     "created_at:desc",
    "title-descending":    "created_at:desc",
    "price-ascending":     "price:asc",
    "price-descending":    "price:desc",
    "created-ascending":   "created_at:asc",
    "created-descending":  "created_at:desc"
  };
  document.addEventListener("click", function (e) {
    var opt = e.target.closest && e.target.closest(".brum-toolbar__sort-option");
    if (!opt) return;
    e.preventDefault();
    // Prefer an explicit Typesense sort on the option (data-brum-sort);
    // otherwise fall back to mapping Shopify's sort_by from the href.
    var brumSort;
    if (opt.dataset.brumSort) {
      brumSort = opt.dataset.brumSort;
    } else {
      var href = opt.getAttribute("href") || "";
      var m = href.match(/[?&]sort_by=([^&#]+)/);
      var shopifySort = m ? decodeURIComponent(m[1]) : "";
      brumSort = SHOPIFY_TO_BRUM_SORT[shopifySort] || DEFAULT_SORT;
    }
    state.sort = brumSort;
    state.page = 1;
    // Sync .is-active across sibling options for visual feedback.
    var list = opt.closest(".brum-toolbar__sort-list");
    if (list) {
      list.querySelectorAll(".brum-toolbar__sort-option")
          .forEach(function (a) { a.classList.remove("is-active"); });
      opt.classList.add("is-active");
    }
    // Collapse the <details> dropdown after selection.
    var det = opt.closest("details");
    if (det) det.removeAttribute("open");
    refresh();
  });
  // On load, highlight the sort option matching the active sort (runs after
  // init has set state.sort from the URL).
  setTimeout(function () {
    document.querySelectorAll(".brum-toolbar__sort-option").forEach(function (a) {
      if (a.dataset.brumSort) {
        a.classList.toggle("is-active", a.dataset.brumSort === state.sort);
      }
    });
  }, 0);
  document.addEventListener("click", function (e) {
    var btn = e.target.closest && e.target.closest('[data-power-unit-toggle] button');
    if (!btn) return;
    setPowerUnit(btn.dataset.unit);
  });
  root.querySelector("[data-chips]").addEventListener("click", function (e) {
    var t = e.target;
    if (t.matches("[data-chip-remove]")) {
      var idx = parseInt(t.dataset.chipRemove, 10);
      var chips = root.querySelector("[data-chips]").__chips || [];
      var c = chips[idx]; if (!c) return;
      if (c.kind === "facet") state.facets[c.facet].delete(c.value);
      if (c.kind === "range") { state.range[c.minKey] = ""; state.range[c.maxKey] = "";
        [c.minKey, c.maxKey].forEach(function (k) { var el = root.querySelector('[data-range="' + k + '"]'); if (el) el.value = ""; }); }
      state.page = 1; refresh();
    } else if (t.matches("[data-chips-clear]")) {
      resetAll();
    }
  });
  root.querySelector("[data-reset]").addEventListener("click", resetAll);

  root.querySelectorAll('.brum__filter:not(.brum__filter--make-model) > .brum__filter-summary')
      .forEach(function (sum) {
        if (sum.querySelector('[data-filter-clear]')) return;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'brum__filter-clear';
        btn.setAttribute('data-filter-clear', '');
        btn.setAttribute('aria-label', 'Clear filter');
        btn.textContent = '×';
        var chev = sum.querySelector('.brum__chev');
        sum.insertBefore(btn, chev || null);
      });

  root.addEventListener("click", function (e) {
    var btn = e.target.closest && e.target.closest("[data-filter-clear]");
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var section = btn.closest("[data-filter-section]");
    if (!section) return;
    var name = section.getAttribute("data-filter-section");
    if (FACET_GROUPS[name]) {
      FACET_GROUPS[name].forEach(function (f) { state.facets[f] = new Set(); });
    } else if (FACETS.indexOf(name) !== -1) {
      state.facets[name] = new Set();
    } else {
      var minKey = name + "_min";
      var maxKey = name + "_max";
      if (RANGES.indexOf(minKey) !== -1) {
        state.range[minKey] = "";
        var minEl = root.querySelector('[data-range="' + minKey + '"]');
        if (minEl) minEl.value = "";
      }
      if (RANGES.indexOf(maxKey) !== -1) {
        state.range[maxKey] = "";
        var maxEl = root.querySelector('[data-range="' + maxKey + '"]');
        if (maxEl) maxEl.value = "";
      }
      updateAllSliderStates();
    }
    state.page = 1;
    refresh();
  });
root.addEventListener("click", function (e) {
    var moreBtn = e.target.closest && e.target.closest("[data-facet-more]");
    if (!moreBtn) return;
    var sec = moreBtn.closest(".brum__filter");
    if (!sec) return;
    var open = sec.classList.toggle("is-expanded");
    moreBtn.setAttribute("aria-expanded", open ? "true" : "false");
  });
  var showMoreBtn = document.querySelector("[data-brum-show-more]");
  if (showMoreBtn) {
    showMoreBtn.addEventListener("click", function () {
      state.page += 1;
      refresh(true); // append
    });
  }

  document.addEventListener("click", function (e) {
    var openBtn = e.target.closest && e.target.closest(".open-map-btn");
    if (openBtn) {
      e.preventDefault();
      var card = openBtn.closest("[data-product-id]") || openBtn.closest("li");
      var productId = card ? card.getAttribute('data-product-id') : null;

      // Find the popup: sibling first, then card descendant, then already hoisted (in body).
      var overlay = openBtn.nextElementSibling;
      if (!overlay || !overlay.classList.contains("map-popup-overlay")) {
        if (card) overlay = card.querySelector(".map-popup-overlay");
      }
      if (!overlay && productId) {
        overlay = document.querySelector(
          '.map-popup-overlay[data-vb-popup-for="' + productId + '"]'
        );
      }

      if (overlay) {
        if (overlay.parentNode !== document.body) {
          if (productId) overlay.setAttribute('data-vb-popup-for', productId);
          document.body.appendChild(overlay);
        }
        overlay.classList.add("is-open");
        document.body.style.overflow = "hidden";
      }
      return;
    }
    var closeBtn = e.target.closest && e.target.closest(".close-map-btn");
    if (closeBtn) {
      e.preventDefault();
      var ov = closeBtn.closest(".map-popup-overlay");
      if (ov) {
        ov.classList.remove("is-open");
        document.body.style.overflow = "";
      }
      return;
    }
    // Click on the backdrop (outside .map-popup) closes the overlay.
    if (e.target.classList && e.target.classList.contains("map-popup-overlay")) {
      e.target.classList.remove("is-open");
      document.body.style.overflow = "";
    }
  });
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    var open = document.querySelector(".map-popup-overlay.is-open");
    if (open) {
      open.classList.remove("is-open");
      document.body.style.overflow = "";
    }
  });
  function resetAll() {
  FACETS.forEach(function (f) { state.facets[f] = new Set(); });
  RANGES.forEach(function (r) {
    state.range[r] = "";
    var el = root.querySelector('[data-range="' + r + '"]');
    if (el) el.value = "";
  });
  state.modelMake = {};
  state.seriesMake = {};
  state.viewAllMakes = new Set();
  state.sort = DEFAULT_SORT;
  state.page = 1;
  // NEW: reset location filter
  state.geo = { active: false, lat: null, lng: null, radius: 50, placeName: null, method: null };
  updateNearMeStatus();
  updateNearMeCenterMarker();
  document.querySelectorAll(".brum-toolbar__sort-option").forEach(function (a) {
    a.classList.remove("is-active");
  });
  refresh();
}
  window.addEventListener("popstate", function () { readUrl(); updateAllSliderStates(); refresh(); });
  // -------- Boot --------
  // Near Me — buttons (Use my location / Pick on map / Clear)
var nearMeGpsBtn = root.querySelector('[data-near-me-use-gps]');
var nearMePickBtn = root.querySelector('[data-near-me-pick-map]');
var nearMeClearBtn = root.querySelector('[data-near-me-clear]');
var nearMeStatusEl = root.querySelector('[data-near-me-status]');
var nearMeCenterMarker = null;
var nearMePickerMode = false;

function reverseGeocode(lat, lng) {
  var url = 'https://nominatim.openstreetmap.org/reverse?format=json&zoom=10&addressdetails=1&lat=' + lat + '&lon=' + lng;
  return fetch(url, { headers: { 'Accept-Language': 'en' } })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      if (!data || !data.address) return null;
      var a = data.address;
      var name = a.city ||
                 a.town ||
                 a.municipality ||
                 a.county ||
                 a.village ||
                 a.hamlet ||
                 a.state ||
                 a.country ||
                 null;
      if (name) name = name.replace(/\s*\(.+?\)\s*/g, '').trim();
      return name;
    })
    .catch(function () { return null; });
}

function applyNearMeLocation(lat, lng, method) {
  state.geo = {
    active: true,
    lat: lat,
    lng: lng,
    radius: state.geo.radius || 50,
    placeName: null,
    method: method || null   
  };
  state.sort = "distance:asc";
  state.page = 1;
  updateNearMeStatus();
  updateNearMeCenterMarker();
  // Reverse-geocode in the background; updates banner when done
  reverseGeocode(lat, lng).then(function (name) {
    if (name) {
      state.geo.placeName = name;
      updateNearMeStatus();
    }
  });
  var mapEl = document.querySelector('[data-brum-map]');
  if (mapEl) { mapEl.style.top = ''; mapEl.style.bottom = ''; mapEl.style.height = ''; }
  var listBtn = document.querySelector('[data-brum-view="list"]');
  if (listBtn) listBtn.click();
  refresh();
}

function clearNearMeLocation() {
  state.geo.active = false;
  if (state.sort === "distance:asc") state.sort = DEFAULT_SORT;
  state.page = 1;
  updateNearMeStatus();
  updateNearMeCenterMarker();
  refresh();
}

function updateNearMeStatus() {
  if (!nearMeStatusEl) return;
  nearMeStatusEl.hidden = !state.geo.active;
  var placeEl = nearMeStatusEl.querySelector('[data-near-me-place]');
  if (placeEl) {
  if (state.geo.placeName) {
    placeEl.innerHTML = 'from <strong>' + escapeHtml(state.geo.placeName) + '</strong>';
  } else {
    placeEl.textContent = '';
  }
}
  // Highlight the active method button
  if (nearMeGpsBtn) {
    nearMeGpsBtn.classList.toggle('is-active', state.geo.active && state.geo.method === 'gps');
  }
  if (nearMePickBtn) {
    nearMePickBtn.classList.toggle('is-active', state.geo.active && state.geo.method === 'map');
  }
}

function updateNearMeCenterMarker() {
  if (!brumMap || typeof L === "undefined") return;
  if (nearMeCenterMarker) {
    brumMap.removeLayer(nearMeCenterMarker);
    nearMeCenterMarker = null;
  }
  if (state.geo.active && state.geo.lat != null && state.geo.lng != null) {
    var icon = L.divIcon({
      className: 'brum-center-marker',
      html: '<div class="brum-center-pin">📍</div>',
      iconSize: [36, 36],
      iconAnchor: [18, 36],
    });
    nearMeCenterMarker = L.marker([state.geo.lat, state.geo.lng], { icon: icon }).addTo(brumMap);
  }
}

function enterPickerMode() {
  nearMePickerMode = true;
  if (brumMap) L.DomUtil.addClass(brumMap.getContainer(), 'brum-picker-mode');
}
function exitPickerMode() {
  nearMePickerMode = false;
  if (brumMap) L.DomUtil.removeClass(brumMap.getContainer(), 'brum-picker-mode');
}


// "Use my location" button
if (nearMeGpsBtn) {
  nearMeGpsBtn.addEventListener('click', function (e) {
    if (!navigator.geolocation) {
      console.warn("[Brum] Geolocation not supported.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        applyNearMeLocation(pos.coords.latitude, pos.coords.longitude, 'gps');
      },
      function (err) {
        console.warn("[Brum] Could not get your location:", err.message || "permission denied");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  });
}

if (nearMePickBtn) {
  nearMePickBtn.addEventListener('click', function () {
    // Close the drawer so its buttons aren't accidentally tappable later
    if (typeof closeDrawer === "function") closeDrawer();
    var mapBtn = document.querySelector('[data-brum-view="map"]');
    if (mapBtn) mapBtn.click();
    setTimeout(function () {
      var mapEl = document.querySelector('[data-brum-map]');
      if (mapEl) {
        mapEl.style.top = '0';
        mapEl.style.bottom = '0';
        mapEl.style.height = '100dvh';
      }
      enterPickerMode();
      if (brumMap) brumMap.invalidateSize();
    }, 200);
  });
}

// Clear button
if (nearMeClearBtn) {
  nearMeClearBtn.addEventListener('click', clearNearMeLocation);
}

var nearMeRadius = root.querySelector('[data-near-me-radius-slider]');
var nearMeRadiusText = root.querySelector('[data-near-me-radius-text]');
var nearMeFill = root.querySelector('[data-near-me-radius-fill]');
function updateNearMeFill() {
  if (!nearMeRadius || !nearMeFill) return;
  var min = +nearMeRadius.min || 0;
  var max = +nearMeRadius.max || 100;
  var val = +nearMeRadius.value || 0;
  var pct = ((val - min) / (max - min)) * 100;
  nearMeFill.style.width = pct + '%';
}
if (nearMeRadius) {
  updateNearMeFill();                                      // initial paint
  nearMeRadius.addEventListener('input', function (e) {
    var r = parseInt(e.target.value, 10);
    state.geo.radius = r;
    if (nearMeRadiusText) nearMeRadiusText.textContent = String(r);
    updateNearMeFill();                                    // live fill update
    if (state.geo.active) {
      state.page = 1;
      scheduleRefresh(250);
    }
  });
}

var brumMap = null;
var brumMapMarkers = [];
function brumInitMap() {
  if (brumMap) return;
  var mapEl = document.querySelector("[data-brum-map]");
  if (!mapEl || typeof L === "undefined") return;
  brumMap = L.map(mapEl).setView([46.15, 14.99], 7); // Slovenia center
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution: "© OpenStreetMap contributors",
    maxZoom: 19,
  }).addTo(brumMap);
  if (state.geo && state.geo.active && state.geo.lat != null && state.geo.lng != null) {
    brumMap.setView([state.geo.lat, state.geo.lng], 10);
  }

  brumMap.on('click', function (e) {
  if (!nearMePickerMode) return;
  applyNearMeLocation(e.latlng.lat, e.latlng.lng, 'map');
  exitPickerMode();
});

  updateNearMeCenterMarker();
}
function brumUpdateMapPins(data) {
  if (!brumMap) return;
  brumMapMarkers.forEach(function (m) { brumMap.removeLayer(m); });
  brumMapMarkers = [];
  var hits = (data && data.hits) || [];
  var bounds = L.latLngBounds([]);
  hits.forEach(function (h) {
    var doc = h.document || h;
    var loc = doc.location;
    if (!Array.isArray(loc) || loc.length !== 2) return;
    var marker = L.marker(loc).addTo(brumMap);
    var img = doc.image_url ? '<img src="' + escapeHtml(doc.image_url) + '" alt="">' : "";
    var price = doc.price ? '€' + Number(doc.price).toLocaleString('de-DE') : "";
    var url = "/products/" + encodeURIComponent(doc.handle || "");
    marker.bindPopup(
      '<div class="brum-map-popup">' +
        img +
        '<div class="brum-map-popup-title">' + escapeHtml(doc.title || "") + '</div>' +
        '<div class="brum-map-popup-price">' + price + '</div>' +
        '<a href="' + url + '">View vehicle</a>' +
      '</div>'
    );
    brumMapMarkers.push(marker);
    bounds.extend(loc);
  });
  if (brumMapMarkers.length > 0 && bounds.isValid()) {
    brumMap.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }
}

// Toggle list/map view
document.querySelectorAll("[data-brum-view]").forEach(function (btn) {
  btn.addEventListener("click", function () {
    var view = btn.dataset.brumView;
    document.querySelectorAll("[data-brum-view]").forEach(function (b) {
      b.classList.toggle("is-active", b === btn);
    });
    var mapEl = document.querySelector("[data-brum-map]");
    var resultsEl = document.querySelector("[data-brum-results]");
    var gridWrapEl = document.querySelector(".main-products-grid");
    var paginatorEl = document.querySelector("[data-brum-paginator]");
    if (view === "map") {
      document.body.classList.add("brum-map-fullscreen");
      if (mapEl) {
        var placeholder = document.getElementById('brum-map-placeholder');
        if (!placeholder) {
          placeholder = document.createElement('div');
          placeholder.id = 'brum-map-placeholder';
          placeholder.hidden = true;
          mapEl.parentNode.insertBefore(placeholder, mapEl);
        }
        document.body.appendChild(mapEl);
        var exitBtn = document.querySelector(".brum-map-exit");
        if (exitBtn) document.body.appendChild(exitBtn);
        mapEl.hidden = false;
      }
      if (resultsEl) resultsEl.hidden = true;
      if (gridWrapEl) gridWrapEl.hidden = true;
      if (paginatorEl) paginatorEl.hidden = true;
      brumInitMap();
      setTimeout(function () { if (brumMap) brumMap.invalidateSize(); }, 100);
      if (lastData) brumUpdateMapPins(lastData);
    } else {
      document.body.classList.remove("brum-map-fullscreen");
      if (mapEl) {
        mapEl.hidden = true;
        var placeholder2 = document.getElementById('brum-map-placeholder');
        if (placeholder2 && placeholder2.parentNode) {
          placeholder2.parentNode.insertBefore(mapEl, placeholder2);
          var exitBtn2 = document.querySelector(".brum-map-exit");
          if (exitBtn2) placeholder2.parentNode.insertBefore(exitBtn2, placeholder2);
          placeholder2.remove();
        }
      }
      if (resultsEl) resultsEl.hidden = false;
      if (gridWrapEl) gridWrapEl.hidden = false;
      if (paginatorEl) paginatorEl.hidden = false;
      // Reopen drawer if user was in picker mode (cancelled picking or picked a location)
      if (nearMePickerMode) {
        exitPickerMode();
        if (typeof openDrawer === "function") {
          setTimeout(openDrawer, 50);
        }
      }
    }
  });
});

  var FACET_LAYOUT = {
    bike: {
      visible: ["make","body_type","price","year","cubic_capacity","power","mileage","vehicle_details__cylinder_count","vehicle_condition"],
      more: ["vehicle_details__stroke_number","vehicle_details__cooling_system","vehicle_details__driving_mode","owners","transmission","fuel_type","colors_group","comfort_group","safety_group","extras_features","near_me","vendor","vehicle_details__emission_class"],
      open: { price:true, cubic_capacity:true, power:true, year:true, mileage:true, vehicle_details__cylinder_count:true, vehicle_details__stroke_number:true, vehicle_details__cooling_system:true, vehicle_details__driving_mode:true },
      inlineRow: ["vehicle_details__cylinder_count"]
    },
    "e-bike": {
      visible: ["make","price","year","body_type","battery_group","vehicle_condition"],
      more: ["frame_group","gear_type","audience","vehicle_details__wheel_size","vehicle_details__motor_position","colors_group","near_me","vendor"],
      open: { price:true, year:true, body_type:true, vehicle_condition:true },
      inlineRow: ["vehicle_condition"]
    }
  };

  function applyFacetLayout() {
    var handle = detectCollectionHandle();
    var layout = handle && FACET_LAYOUT[handle.toLowerCase()];
    if (!layout) return; // cars / unlisted collections: keep markup order
    var drawerBody = root.querySelector(".brum__drawer-body");
    var moreToggle = root.querySelector("[data-toggle-more]");
    var moreContainer = root.querySelector("[data-more-filters]");
    if (!drawerBody || !moreToggle || !moreContainer) return;

    var bySection = {};
    var all = root.querySelectorAll("[data-filter-section]");
    for (var i = 0; i < all.length; i++) bySection[all[i].dataset.filterSection] = all[i];
    (layout.inlineRow || []).forEach(function (sec) {
      var el = bySection[sec];
      if (!el) return;
      el.classList.add("brum__filter--inline");
      var ul = el.querySelector('[data-options-list="' + sec + '"]');
      if (ul) ul.classList.add("brum__facet-list--row");
      var chev = el.querySelector(".brum__chev");
      if (chev) chev.style.display = "none";
    });

    var listed = {};
    var openMap = layout.open || {};
    function place(sec, container, before) {
      var el = bySection[sec];
      if (!el) return;
      listed[sec] = true;
      el.style.display = "";
      // Keep subfacets inside a placed group visible too.
      el.querySelectorAll("[data-filter-section]").forEach(function (c) {
        listed[c.dataset.filterSection] = true;
        c.style.display = "";
      });
      el.open = el.classList.contains("brum__filter--inline") ? true : !!openMap[sec];
      if (before) container.insertBefore(el, before);
      else container.appendChild(el);
    }
    layout.visible.forEach(function (sec) { place(sec, drawerBody, moreToggle); });
    layout.more.forEach(function (sec) { place(sec, moreContainer, null); });
    // Hide any facet this collection doesn't use.
    for (var sec in bySection) { if (!listed[sec]) bySection[sec].style.display = "none"; }
  }

  // Snapshot the full value set for grey-out facets ONCE (unfiltered) so we can
  // dim unavailable options instead of dropping them. Cached for the session.
  function primeFacetUniverse() {
    var col = detectCollectionHandle();
    var url = PROXY_BASE + "/search?per_page=0" + (col ? "&collection=" + encodeURIComponent(col) : "");
    fetch(url, { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        Object.keys(data.facets || {}).forEach(function (facet) {
          var items = (data.facets && data.facets[facet]) || [];
          FACET_UNIVERSE[facet] = items
            .map(function (it) { return it.value; })
            .filter(function (v) { return v !== "" && v != null; });
        });
        window.__FU = FACET_UNIVERSE;
        if (!lastData) {
          // SSR path: no grid fetch ran, so seed the drawer from this per_page=0 response.
          lastData = data;
          // Honest count: show the true searchable total from Typesense, not Shopify's SSR count.
          try { if (data.found != null) animateCount(data.found); } catch (e) {}
          try { renderFacets(data); } catch (e) {}
          try { renderHistogram(data); } catch (e) {}
          try { syncAllSliderBounds(data); } catch (e) {}
          try { updateAllSliderStates(); } catch (e) {}
        } else {
          renderFacets(lastData);
        }
      })
      .catch(function (e) { console.error("[Brum] facet universe prime failed:", e); });
  }
  function hasActiveFilters() {
    for (var f in state.facets) { if (state.facets[f] && state.facets[f].size > 0) return true; }
    for (var r in state.range) { if (state.range[r] !== "" && state.range[r] != null) return true; }
    if (state.q) return true;
    if (state.sort && state.sort !== DEFAULT_SORT) return true;
    if (state.page > 1) return true;
    return false;
  }
  function boot() {
    applyFacetLayout();
    setupSliders();
    readUrl();
    updateAllSliderStates();

    var ssrGrid = document.querySelector("[data-brum-results]");
    var ssrReady = ssrGrid && ssrGrid.hasAttribute("data-brum-ssr") && ssrGrid.children.length > 0;
    if (ssrReady && !hasActiveFilters()) {
      // Default unfiltered page is already server-rendered — don't refetch.
      var total = parseInt(ssrGrid.getAttribute("data-brum-total"), 10) || ssrGrid.children.length;
      animateCount(total);
      updatePaginator(ssrGrid.children.length, total);
      Array.prototype.forEach.call(ssrGrid.children, localizeCard);
      var _rSSR = document.querySelector(".brum"); if (_rSSR) _rSSR.removeAttribute("data-brum-booting");
    } else {
      refresh();
    }
    primeFacetUniverse();

    var moreToggle = root.querySelector('[data-toggle-more]');
    var moreContainer = root.querySelector('[data-more-filters]');
    if (moreToggle && moreContainer) {
      function setMoreExpanded(expanded) {
        moreToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        moreContainer.hidden = !expanded;
        var labelEl = moreToggle.querySelector('.brum__more-toggle-label');
        if (labelEl) { var Um = window.BRUM_UI || {}; labelEl.textContent = expanded ? (Um.showFewer || 'Show fewer filters') : (Um.showMore || 'Show more filters'); }
        if (expanded) moreContainer.parentNode.insertBefore(moreToggle, moreContainer.nextSibling);
        else moreContainer.parentNode.insertBefore(moreToggle, moreContainer);
      }
      moreToggle.addEventListener('click', function () {
        var expanded = moreToggle.getAttribute('aria-expanded') === 'true';
        setMoreExpanded(!expanded);
      });
      var hiddenSections = moreContainer.querySelectorAll('[data-filter-section]');
      for (var i = 0; i < hiddenSections.length; i++) {
        var section = hiddenSections[i].dataset.filterSection;
        if (state.facets[section] && state.facets[section].size > 0) {
          setMoreExpanded(true);
          break;
        }
      }
    }
   
  }
  setTimeout(function () {
    var r = document.querySelector(".brum");
    if (r) r.removeAttribute("data-brum-booting");
  }, 3000);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  // ────────────────────────────────────────────────────────────────
  // BACKUP RENDER — if for any reason boot/refresh fails to render
  // cards within a second of the page being ready, we force a
  // direct render here. This runs INSIDE the same IIFE so it has
  // access to all helpers (renderResults, applyCardData, etc).
  // ────────────────────────────────────────────────────────────────
  function brumBackup() {
    var results = document.querySelector("[data-brum-results]");
    if (!results || results.children.length > 0) return;
    var col = (location.pathname.match(/\/collections\/([^\/?#]+)/) || [])[1] || "";
    var url = PROXY_BASE + "/search?" + buildParams().toString();
    fetch(url, { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        lastData = data;
        try {
          renderResults(data, false);
        } catch (e) {
          console.error("[Brum] BACKUP renderResults threw:", e);
          // Last-resort: clone+append every hit raw, no data swap.
          var tpl = document.getElementById("brum-card-template");
          if (!tpl) return;
          results.innerHTML = "";
          (data.hits || []).forEach(function (h) {
            try {
              var clone = tpl.content.cloneNode(true);
              var li = document.createElement("li");
              while (clone.firstChild) li.appendChild(clone.firstChild);
              results.appendChild(li);
            } catch (err) {
              console.error("[Brum] BACKUP card error:", err);
            }
          });
        }
      })
      .catch(function (e) {
        console.error("[Brum] BACKUP fetch failed:", e);
      });
  }
  if (document.readyState === "complete") {
    setTimeout(brumBackup, 800);
  } else {
    window.addEventListener("load", function () { setTimeout(brumBackup, 800); });
  }
})();